import { CheckCircle, ChevronLeft, ChevronRight, FileSignature, ZoomIn, ZoomOut, MessageSquare, X, User, Mail, Phone, Calendar, Hash, Type, Pen, FileText, Layers } from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { documentService } from '../services/documentService';
import { signatureService } from '../services/signatureService';
import { formFieldService } from '../services/formFieldService';
import { API_URL } from '../utils/apiConfig';
import { 
  invalidateDocumentQueries, 
  invalidateSignatureQueries
} from '../utils/queryClient';
import { documentCacheService } from '../services/documentCacheService';
import SignatureHistory from './SignatureHistory';
import SignaturePlacement from './SignaturePlacement';
// import DirectSignatureImage from './DirectSignatureImage';
import SignatureRequestsPanel from './SignatureRequestsPanel';
import '../pdfjs-config';

// Update worker URL to use the correct version from pdfjs-dist
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;

// Apple-inspired design system matching DocumentVersionHistory.jsx
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

// Form field type configuration
const FORM_FIELD_TYPES = {
  name: { label: 'Name', icon: User, placeholder: 'Enter full name' },
  title: { label: 'Title', icon: Hash, placeholder: 'Enter job title' },
  email: { label: 'Email', icon: Mail, placeholder: 'Enter email address' },
  phone: { label: 'Phone', icon: Phone, placeholder: 'Enter phone number' },
  date: { label: 'Date', icon: Calendar, placeholder: 'MM/DD/YYYY' },
  text: { label: 'Text', icon: Type, placeholder: 'Enter text' }
};

/**
 * DocumentViewer component for displaying and interacting with PDF documents
 */
const DocumentViewer = ({ 
  documentId, 
  canView = true, 
  canEdit = false, 
  canComment = false, 
  canSign = false,
  sharePermission = "view", // Add this to directly accept the permission value from the uploader
  isOwner = false, 
  onEditClick, 
  onVersionHistoryClick, 
  onSignatureApplied,
  refreshTrigger, // Add this to trigger annotation refresh from parent
  isReviewMode = false, // New prop for review mode
  presignedFields = new Set(), // Set of field IDs that are presigned
  onFieldPresign, // Callback for presigning fields
  showSignatures = false, // Control signature panel visibility from parent
  showSignatureRequests = false, // Control signature requests panel visibility from parent
  onSignatureViewClose, // Callback to close signature views
  onSignatureFieldsInfoUpdate, // Callback to send signature field info to parent
  calculateRemainingFields, // Function to calculate remaining fields
  navigateToNextField, // Function to navigate to next field
  isHeaderVisible = true // Whether the header is visible (for floating button)
}) => {
  // Ensure cache service has an active query client before any use
  // const cache = useDocumentCache();
  const navigate = useNavigate();
  const { user } = useAuth();
  const documentRef = useRef(null);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [initialScaleSet, setInitialScaleSet] = useState(false);
  
  // View mode state for pagination vs continuous scroll
  const [viewMode, setViewMode] = useState('paged'); // 'paged' | 'continuous'
  const [visiblePages, setVisiblePages] = useState(new Set([1]));
  const [currentVisiblePage, setCurrentVisiblePage] = useState(1);
  const [pageHeights, setPageHeights] = useState({});
  // const [isLoadingPages, setIsLoadingPages] = useState(false);
  const pageRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const [showSignaturePlacement, setShowSignaturePlacement] = useState(false);
  // const [showSignatureHistory, setShowSignatureHistory] = useState(false);
  const [selectedSignatureField, setSelectedSignatureField] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [annotations, setAnnotations] = useState([]);
  const [showAnnotations] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  // const [pendingOperation, setPendingOperation] = useState(null);
  const [appliedSignatures, setAppliedSignatures] = useState([]);
  const [allRequiredFieldsSigned, setAllRequiredFieldsSigned] = useState(false);
  const [justSignedFields, setJustSignedFields] = useState([]);
  const [signatureRequestId, setSignatureRequestId] = useState(null);
  const [signatureToken, setSignatureToken] = useState(null);
  const [hasSignatureRequest, setHasSignatureRequest] = useState(false);
  
  // State for signature fields and existing signatures
  const [signatureFields, setSignatureFields] = useState([]);
  const [existingSignatures, setExistingSignatures] = useState([]);
  
  // New unified signature state for better synchronization
  const [signatureState, setSignatureState] = useState({
    existingSignatures: [],
    signatureFields: [],
    appliedSignatures: [],
    fieldToSignatureMap: new Map(), // Track which fields have signatures
    lastUpdated: null,
    isLoading: false
  });
  
  // State for form fields and existing form fields
  const [formFields, setFormFields] = useState([]);
  const [existingFormFields, setExistingFormFields] = useState([]);
  const [formFieldValues, setFormFieldValues] = useState({});
  const [editingFormField, setEditingFormField] = useState(null);
  const [formFieldRequestId, setFormFieldRequestId] = useState(null);
  const [hasFormFieldRequest, setHasFormFieldRequest] = useState(false);
  const [completedFormFields, setCompletedFormFields] = useState(new Set());
  const [isDocumentFinalized, setIsDocumentFinalized] = useState(false);

  // Track PDF page dimensions for proper coordinate conversion
  const [pdfPageDimensions, setPdfPageDimensions] = useState(null);
  const [renderedPageDimensions, setRenderedPageDimensions] = useState(null);
  const [allPageDimensions, setAllPageDimensions] = useState({});

  // Set up Intersection Observer for continuous mode page tracking
  useEffect(() => {
    if (viewMode !== 'continuous' || !numPages) return;
    
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // Create new observer for page tracking
    const options = {
      root: scrollContainerRef.current || documentRef.current,
      rootMargin: '-40% 0px -40% 0px', // Consider page visible when 20% is showing
      threshold: 0
    };
    
    observerRef.current = new IntersectionObserver((entries) => {
      const visiblePageNumbers = new Set();
      let mostVisiblePage = null;
      let maxVisibility = 0;
      
      entries.forEach(entry => {
        const pageNum = parseInt(entry.target.dataset.pageNumber);
        if (entry.isIntersecting) {
          visiblePageNumbers.add(pageNum);
          
          // Track the most visible page
          if (entry.intersectionRatio > maxVisibility) {
            maxVisibility = entry.intersectionRatio;
            mostVisiblePage = pageNum;
          }
        }
      });
      
      if (visiblePageNumbers.size > 0) {
        setVisiblePages(visiblePageNumbers);
      }
      
      if (mostVisiblePage !== null && mostVisiblePage !== currentVisiblePage) {
        setCurrentVisiblePage(mostVisiblePage);
        setPageNumber(mostVisiblePage); // Update page number for indicator
      }
    }, options);
    
    // Observe all page elements
    Object.keys(pageRefs.current).forEach(pageNum => {
      const element = pageRefs.current[pageNum];
      if (element) {
        observerRef.current.observe(element);
      }
    });
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [viewMode, numPages, currentVisiblePage]);

  // Navigate to specific field with scrolling and expansion
  const navigateToField = (field) => {
    if (!field) return;
    
    // Handle form fields differently
    if (field.type === 'form_field') {
      console.log('DEBUG: Navigating to form field:', field);
      
      // Parse coordinates for form field
      let coordinates = null;
      if (field.coordinates) {
        try {
          if (typeof field.coordinates === 'string') {
            coordinates = JSON.parse(field.coordinates);
          } else {
            coordinates = field.coordinates;
          }
        } catch (e) {
          console.error('Failed to parse form field coordinates:', e);
          return;
        }
      }
      
      if (!coordinates) {
        console.warn('Form field has no coordinates:', field);
        return;
      }
      
      // Navigate to the correct page
      const targetPage = coordinates.pageNumber || coordinates.page || 1;
      console.log(`DEBUG: Navigating to page ${targetPage} for form field ${field.id}`);
      
      if (viewMode === 'continuous') {
        scrollToPage(targetPage);
      } else {
        setPageNumber(targetPage);
      }
      
      // Wait for page to render, then activate form field for editing
      setTimeout(() => {
        setEditingFormField(field.fieldId || field.id);
        
        // Scroll to the form field position
        const pageElement = viewMode === 'continuous' 
          ? pageRefs.current[targetPage]?.querySelector('.react-pdf__Page__canvas')
          : documentRef.current?.querySelector('.react-pdf__Page');
          
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          const x = coordinates.x || coordinates.pdfX || 0;
          const y = coordinates.y || coordinates.pdfY || 0;
          
          // Calculate absolute position using existing helper
          const position = getAbsolutePosition(x, y, 150, 30);
          
          // Scroll to the field position
          window.scrollTo({
            left: pageRect.left + position.left - window.innerWidth / 2,
            top: pageRect.top + position.top - window.innerHeight / 2,
            behavior: 'smooth'
          });
        }
      }, viewMode === 'continuous' ? 100 : 300);
      
      return;
    }
    
    // Handle signature fields (existing logic)
    // Convert PDF-space coordinates to pixel-space for SignaturePlacement
    // let convertedField = field;
    try {
      const pageElement = viewMode === 'continuous' 
        ? pageRefs.current[field.page || 1]?.querySelector('.react-pdf__Page__canvas')
        : documentRef.current?.querySelector('.react-pdf__Page');
      const pageRect = pageElement?.getBoundingClientRect();
      if (pdfPageDimensions && pageRect) {
        const scaleX = pageRect.width / pdfPageDimensions.width;
        const scaleY = pageRect.height / pdfPageDimensions.height;
        // convertedField = {
        //   ...field,
        //   x: (field.x || 0) * scaleX,
        //   y: (field.y || 0) * scaleY,
        //   width: (field.width || 200) * scaleX,
        //   height: (field.height || 70) * scaleY,
        // };
      }
    } catch (e) {
      console.warn('Field coordinate conversion failed, using raw field:', e);
    }

    // Navigate to the correct page
    const targetPage = field.page || 1;
    console.log(`DEBUG: Navigating to page ${targetPage} for field ${field.id}`);
    
    if (viewMode === 'continuous') {
      // In continuous mode, scroll to the page containing the field
      scrollToPage(targetPage);
    } else {
      setPageNumber(targetPage);
    }
    
    // Wait for page to render, then scroll to field and expand it
    setTimeout(() => {
      const fieldElement = documentRef.current?.querySelector(`[data-field-id="${field.id}"]`);
      if (fieldElement) {
        console.log(`DEBUG: Found field element for ${field.id}, scrolling and highlighting`);
        
        // Scroll field into view with smooth animation
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'center'
        });
        
        // Add hover-like expansion effect
        setTimeout(() => {
          fieldElement.style.transform = 'scale(1.1)';
          fieldElement.style.zIndex = '1000';
          fieldElement.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4), 0 0 0 3px rgba(59, 130, 246, 0.2)';
          fieldElement.style.transition = 'all 0.3s ease-out';
          
           // Remove expansion after a moment to show the field
           setTimeout(() => {
             fieldElement.style.transform = 'scale(1)';
             fieldElement.style.zIndex = '50'; // Keep original z-index for clickability
             fieldElement.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3), 0 0 0 2px rgba(59, 130, 246, 0.15)';
           }, 2000);
        }, 500);
      } else {
        console.log(`DEBUG: Field element not found for ${field.id}, waiting longer or using fallback`);
        
        // Wait a bit longer for the page to fully render
        setTimeout(() => {
          const retryFieldElement = documentRef.current?.querySelector(`[data-field-id="${field.id}"]`);
          if (retryFieldElement) {
            console.log(`DEBUG: Found field element on retry for ${field.id}`);
            retryFieldElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'center'
            });
            
            setTimeout(() => {
              retryFieldElement.style.transform = 'scale(1.1)';
              retryFieldElement.style.zIndex = '1000';
              retryFieldElement.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4), 0 0 0 3px rgba(59, 130, 246, 0.2)';
              retryFieldElement.style.transition = 'all 0.3s ease-out';
              
              setTimeout(() => {
                retryFieldElement.style.transform = 'scale(1)';
                retryFieldElement.style.zIndex = '50';
                retryFieldElement.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3), 0 0 0 2px rgba(59, 130, 246, 0.15)';
              }, 2000);
            }, 500);
          } else {
            console.log(`DEBUG: Still can't find field element for ${field.id}, skipping visual effects but page should be correct`);
            // Don't open signature placement - just ensure we're on the right page
            // The user can click the field manually
          }
        }, 1000);
      }
    }, 300);
  };

  // Signature state management helpers
  const createFieldToSignatureMap = (signatures, fields) => {
    const map = new Map();
    
    // First, map all existing signatures to their fields
    signatures.forEach(sig => {
      if (sig.fieldId) {
        // Handle both string and number field IDs
        const fieldId = String(sig.fieldId);
        map.set(fieldId, sig);
        console.log(`DEBUG: Mapped signature ${sig.id} to field ${fieldId}`);
      }
    });
    
    // Then, check fields for signedAt status (for multi-user scenarios)
    fields.forEach(field => {
      if (field.signedAt && field.id) {
        const fieldId = String(field.id);
        if (!map.has(fieldId)) {
          // Create a placeholder signature entry for signed fields without signature objects
          map.set(fieldId, {
            id: `field-signed-${fieldId}`,
            fieldId: fieldId,
            signedAt: field.signedAt,
            signedIP: field.signedIP,
            userId: field.userId,
            isFieldSignedMarker: true // Flag to distinguish from actual signatures
          });
          console.log(`DEBUG: Mapped signed field ${fieldId} without signature object`);
        }
      }
    });
    
    console.log(`DEBUG: Created field-to-signature map with ${map.size} entries:`, Array.from(map.keys()));
    return map;
  };

  const updateSignatureState = (newSignatures, newFields, newAppliedSignatures = null) => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
      console.log('DEBUG: updateSignatureState called with:', {
        signatures: newSignatures?.length || 0,
        fields: newFields?.length || 0,
        applied: newAppliedSignatures?.length || 0
      });
    }

    setSignatureState(prevState => {
      const updatedSignatures = newSignatures || prevState.existingSignatures;
      const updatedFields = newFields || prevState.signatureFields;
      const updatedApplied = newAppliedSignatures !== null ? newAppliedSignatures : prevState.appliedSignatures;
      
      const fieldToSignatureMap = createFieldToSignatureMap(updatedSignatures, updatedFields);
      
      const newState = {
        existingSignatures: updatedSignatures,
        signatureFields: updatedFields,
        appliedSignatures: updatedApplied,
        fieldToSignatureMap,
        lastUpdated: Date.now(),
        isLoading: false
      };
      
      console.log('DEBUG: Updated signature state:', {
        existingSignatures: newState.existingSignatures.length,
        signatureFields: newState.signatureFields.length,
        appliedSignatures: newState.appliedSignatures.length,
        mappedFields: newState.fieldToSignatureMap.size
      });
      
      return newState;
    });
  };

  const isFieldSigned = (fieldId) => {
    if (!fieldId) return false;
    const fieldIdStr = String(fieldId);
    const hasMappedSignature = signatureState.fieldToSignatureMap.has(fieldIdStr);
    
    // Additional check: look for signedAt in the field data itself
    const field = signatureState.signatureFields.find(f => String(f.id) === fieldIdStr);
    const hasSignedAtData = field && field.signedAt;
    
    const result = hasMappedSignature || hasSignedAtData;
    console.log(`DEBUG: isFieldSigned(${fieldId}): ${result} (mapped: ${hasMappedSignature}, signedAt: ${hasSignedAtData})`);
    return result;
  };

  // Add missing state variables
  const [annotationsLoading, setAnnotationsLoading] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Sync legacy state with unified signature state for backward compatibility
  useEffect(() => {
    if (signatureState.lastUpdated) {
      console.log('DEBUG: Syncing legacy state with unified signature state');
      
      // Keep the old state variables in sync to prevent breaking existing functionality
      if (signatureState.existingSignatures.length > 0) {
        setExistingSignatures({ signatures: signatureState.existingSignatures });
      }
      
      if (signatureState.signatureFields.length > 0) {
        setSignatureFields(signatureState.signatureFields);
      }
      
      if (signatureState.appliedSignatures.length !== appliedSignatures.length) {
        setAppliedSignatures(signatureState.appliedSignatures);
      }
    }
  }, [signatureState.lastUpdated, signatureState.existingSignatures, signatureState.signatureFields, signatureState.appliedSignatures]);
  const [pdfBlob, setPdfBlob] = useState(null);

  const [hasReadContract, setHasReadContract] = useState(false);

  // Add new state at the top:
  const [editingSignatureField, setEditingSignatureField] = useState(null);

  // Helper to convert PDF coordinates to absolute positioning relative to the page
  const getAbsolutePosition = (pdfX, pdfY, pdfWidth, pdfHeight) => {
    // Use ACTUAL PDF page dimensions (same as PDF embedding service)
    // Instead of hardcoded 612x792, use the real dimensions from pdfPageDimensions
    let basePdfWidth, basePdfHeight;
    
    if (pdfPageDimensions) {
      // Use actual PDF dimensions (same as PDF embedding service)
      basePdfWidth = pdfPageDimensions.width;
      basePdfHeight = pdfPageDimensions.height;
      console.log(`Using actual PDF dimensions: ${basePdfWidth}x${basePdfHeight}`);
    } else {
      // Fallback to standard letter size (only if PDF dimensions not available)
      basePdfWidth = 612;
      basePdfHeight = 792;
      console.log(`Using fallback PDF dimensions: ${basePdfWidth}x${basePdfHeight}`);
    }

    // Get the actual PDF page element for relative positioning (same as rendering functions)
    const pageElement = documentRef.current?.querySelector('.react-pdf__Page');
    if (!pageElement) {
      console.warn('PDF page element not found for positioning calculation');
      return { left: '0px', top: '0px', width: '0px', height: '0px' };
    }

    const pageRect = pageElement.getBoundingClientRect();
    
    // Calculate page offsets (same logic as in rendering functions)
    let pageOffsetX = 0;
    let pageOffsetY = 0;
    
    if (viewMode !== 'continuous') {
      // In paged mode, calculate relative to document container
      const containerRect = documentRef.current?.getBoundingClientRect();
      pageOffsetX = pageRect.left - containerRect.left;
      pageOffsetY = pageRect.top - containerRect.top;
    }
    
    // Calculate scale factors
    const scaleX = pageRect.width / basePdfWidth;
    const scaleY = pageRect.height / basePdfHeight;
    
    // Convert PDF coordinates to pixel coordinates
    const pixelX = pdfX * scaleX;
    const pixelY = pdfY * scaleY;
    const pixelWidth = pdfWidth * scaleX;
    const pixelHeight = pdfHeight * scaleY;
    
    console.log(`PDF coords: (${pdfX}, ${pdfY}) ${pdfWidth}x${pdfHeight}`);
    console.log(`Pixel coords: (${pixelX}, ${pixelY}) ${pixelWidth}x${pixelHeight}`);
    console.log(`Scale factors: ${scaleX}x${scaleY}`);
    console.log(`Page dimensions: ${pageRect.width}x${pageRect.height}`);
    console.log(`Page offsets: x=${pageOffsetX}, y=${pageOffsetY}`);
    
    return {
      left: `${pageOffsetX + pixelX}px`,
      top: `${pageOffsetY + pixelY}px`,
      width: `${pixelWidth}px`,
      height: `${pixelHeight}px`
    };
  };

  // Update parent with signature field information and form fields
  useEffect(() => {
    if (onSignatureFieldsInfoUpdate && user) {
      // Get current user's signature fields
      const currentUserSignatureFields = signatureFields.filter(field => field.userId === user.id);
      
      // Get current user's form fields (from existingFormFields)
      const currentUserFormFields = [];
      if (existingFormFields && existingFormFields.formFields) {
        existingFormFields.formFields.forEach(formField => {
          if (formField.userEmail?.toLowerCase() === user.email?.toLowerCase()) {
            currentUserFormFields.push({
              id: formField.fieldId || formField.id,
              fieldId: formField.fieldId || formField.id,
              type: 'form_field',
              fieldType: formField.fieldType,
              coordinates: formField.coordinates,
              userEmail: formField.userEmail,
              fieldValue: formField.fieldValue,
              completedAt: formField.completedAt,
              isCompleted: !!formField.fieldValue || completedFormFields.has(formField.fieldId || formField.id)
            });
          }
        });
      }
      
      // Combine signature fields and form fields for navigation
      const allCurrentUserFields = [...currentUserSignatureFields, ...currentUserFormFields];
      
      onSignatureFieldsInfoUpdate({
        user,
        signatureFields,
        formFields: currentUserFormFields,
        currentUserFields: allCurrentUserFields,
        appliedSignatures,
        isFieldSigned,
        isFormFieldCompleted: (fieldId) => {
          const isFilled = completedFormFields.has(fieldId) || 
            (existingFormFields?.formFields?.find(f => (f.fieldId || f.id) === fieldId)?.fieldValue);
          // For navigation purposes, consider form fields completed when filled
          // They will show as green only after document finalization
          return !!isFilled;
        },
        onNavigateToField: navigateToField
      });
    }
  }, [signatureFields, existingFormFields, completedFormFields, isDocumentFinalized, user, signatureState.fieldToSignatureMap, appliedSignatures, onSignatureFieldsInfoUpdate]);

  // Function to fetch annotations for the document
  const fetchAnnotations = async () => {
    try {
      setAnnotationsLoading(true);
      
      const params = new URLSearchParams(window.location.search);
      const signatureToken = params.get('token');
      
      const headers = {};
      if (signatureToken) {
        headers.Authorization = `Bearer ${signatureToken}`;
      } else if (user?.token) {
        headers.Authorization = `Bearer ${user.token}`;
      }
      
      console.log(`Fetching annotations for document ${documentId}...`);
      const response = await axios.get(`${API_URL}/documents/${documentId}/redlining/annotations`, { headers });
      
      console.log('Annotation API response:', response.data);
      
      if (response.data && response.data.annotations) {
        const annotations = response.data.annotations;
        setAnnotations(annotations);
        console.log('Loaded annotations:', annotations);
        console.log('AI annotations with searchText:', annotations.filter(a => a.aiGenerated && a.content?.searchText));
        console.log('Annotations requiring text search:', annotations.filter(a => a.content?.requiresTextSearch));
      } else {
        console.log('No annotations found in response');
        setAnnotations([]);
      }
    } catch (error) {
      console.error('Error fetching annotations:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      // Don't show error to user, just log it
    } finally {
      setAnnotationsLoading(false);
    }
  };
  
  // Main document and signatures loading
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        // CRITICAL FIX: Clear applied signatures when loading document to prevent cross-user signature display
        setAppliedSignatures([]);
        
        // Check for signature token in URL
        const params = new URLSearchParams(window.location.search);
        const signatureToken = params.get('token');
        
        // Load document data
        try {
          const documentData = await documentService.getDocument(documentId, signatureToken);
          if (!documentData) {
            setError('Document not found or access denied');
            setLoading(false);
            return;
          }
          setDocument(documentData);
          
          // Fetch the file as a blob to avoid CORS issues
          try {
            // Add cache-busting parameter for voided documents or when document was recently updated
            const cacheBuster = documentData.status === 'voided' || documentData.voidedAt 
              ? `&t=${new Date(documentData.updatedAt || documentData.voidedAt || Date.now()).getTime()}`
              : '';
            
            const fileUrl = signatureToken 
              ? `${API_URL}/documents/${documentId}/file?token=${signatureToken}&skipEmbed=true${cacheBuster}`
              : `${API_URL}/documents/${documentId}/file?skipEmbed=true${cacheBuster}`;
            
            console.log(`Fetching document file from: ${fileUrl}`);
            
            const headers = {};
            if (!signatureToken && user?.token) {
              headers['Authorization'] = `Bearer ${user.token}`;
            }
            
            const response = await axios.get(fileUrl, {
              responseType: 'blob',
              headers
            });
            
            console.log("File fetch successful, content type:", response.headers['content-type']);
            
            // Get the correct MIME type from response if available
            const contentType = response.headers['content-type'] || 'application/pdf';
            const fileBlob = new Blob([response.data], { type: contentType });
            const fileObjectUrl = URL.createObjectURL(fileBlob);
            setPdfBlob(fileBlob);
            setFileUrl(fileObjectUrl);
            setPdfError(false);
          } catch (fileError) {
            console.error('Error fetching file:', fileError);
            // Log more detailed error information
            if (fileError.response) {
              console.error('Server returned error status:', fileError.response.status);
              console.error('Server error details:', fileError.response.data);
            }
            setPdfError(true);
          }
          
          // Debug permissions
          const params = new URLSearchParams(window.location.search);
          const hasSignatureToken = !!params.get('token');
          const canUserSignResult = isOwner || hasSignatureRequest || sharePermission === "sign" || canSign || hasSignatureToken;
          
          console.log('Document permissions:', {
            isOwner,
            sharePermission,
            canView,
            canEdit,
            canComment,
            canSign,
            hasSignatureToken,
            hasSignatureRequest,
            canUserSignResult
          });
        } catch (err) {
          console.error('Error loading document:', err);
          setError(err.message || 'Failed to load document');
          setLoading(false);
          return;
        }
        
        // Check for signature request in URL
        const requestId = params.get('requestId');
        
        if (requestId) {
          setSignatureRequestId(requestId);
          setHasSignatureRequest(true);
          try {
            // Mark request as viewed
            const viewResponse = await signatureService.viewSignatureRequest(requestId);
            if (viewResponse?.signatureFields) {
              // Transform signature fields to include userId
              const transformedFields = viewResponse.signatureFields.map(field => ({
                ...field,
                userId: viewResponse.userId,
                userName: viewResponse.userName,
                userEmail: viewResponse.userEmail
              }));
              setSignatureFields(transformedFields);
            }
          } catch (err) {
            console.error('Error viewing signature request:', err);
          }
        }
        
        // Load existing signatures with improved synchronization
        try {
          console.log('DEBUG: Starting signature data load...');
          setSignatureState(prev => ({ ...prev, isLoading: true }));
          
          const signatureData = await signatureService.getDocumentSignatures(documentId, signatureToken);
          console.log('DEBUG: Raw signature data received:', signatureData);
          
          let signatures = [];
          let allSignatureFields = [];
          
          if (signatureData?.signatures?.length > 0) {
            signatures = signatureData.signatures;
            console.log('DEBUG: Loaded signatures:', signatures.map(s => ({ id: s.id, fieldId: s.fieldId })));
            setExistingSignatures({ signatures });
          }
          
          // Process any signature requests
          if (signatureData?.requests?.length > 0) {
            // Transform all signature fields from all requests
            allSignatureFields = signatureData.requests.flatMap(req => {
              if (req.signatureFields) {
                return req.signatureFields.map(field => ({
                  ...field,
                  userId: req.userId,
                  userName: req.userName,
                  userEmail: req.userEmail
                }));
              }
              return [];
            });
            
            console.log('DEBUG: All signature fields loaded:', allSignatureFields);
            console.log('DEBUG: Fields with signedAt:', allSignatureFields.filter(f => f.signedAt));
            console.log('DEBUG: Fields with signedIP:', allSignatureFields.filter(f => f.signedIP));
            console.log('DEBUG: Sample field data:', allSignatureFields[0]);
            setSignatureFields(allSignatureFields);
            
            // Check if current user has a pending request
            const currentUserId = user?.id;
            if (currentUserId) {
              const userRequest = signatureData.requests.find(req => 
                req.userId === currentUserId && 
                (req.status === 'pending' || req.status === 'viewed')
              );
              
              if (userRequest) {
                setSignatureRequestId(userRequest.requestId);
                setHasSignatureRequest(true);
              }
            }
          }
          
          // Update unified signature state with synchronized data
          updateSignatureState(signatures, allSignatureFields, []);
          console.log('DEBUG: Signature data loading completed successfully');
          
        } catch (err) {
          console.error('Error loading signatures:', err);
          setSignatureState(prev => ({ ...prev, isLoading: false }));
        }
        
        // Load form field requests and existing form fields (EXACT same pattern as signatures)
        try {
          const formFieldData = await formFieldService.getDocumentFormFieldRequests(documentId);
          if (formFieldData?.formFields?.length > 0) {
            setExistingFormFields({ formFields: formFieldData.formFields });
          }
          
          // Process any form field requests (same pattern as signature requests)
          if (formFieldData?.requests?.length > 0) {
            // Transform all form fields from all requests
            const allFormFields = formFieldData.requests.flatMap(req => {
              if (req.formFields) {
                return req.formFields.map(field => ({
                  ...field,
                  userId: req.fillerId,
                  userName: req.filler?.name,
                  userEmail: req.filler?.email
                }));
              }
              return [];
            });
            
            console.log('DEBUG: All form fields loaded:', allFormFields);
            setFormFields(allFormFields);
            
            // Check if current user has a pending request
            const currentUserId = user?.id;
            if (currentUserId) {
              const userRequest = formFieldData.requests.find(req => 
                req.fillerId === currentUserId && 
                (req.status === 'pending' || req.status === 'viewed')
              );
              
              if (userRequest) {
                setFormFieldRequestId(userRequest.id);
                setHasFormFieldRequest(true);
              }
            }
          }
        } catch (err) {
          console.error('Error loading form field requests:', err);
        }

        // Load redlining annotations
        try {
          await fetchAnnotations();
        } catch (err) {
          console.error('Error loading annotations:', err);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error in document fetch process:', err);
        setError('Failed to load document. Please try again.');
        setLoading(false);
      }
    };
    
    if (documentId) {
      fetchDocument();
    }
    
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [documentId, user?.id, refreshTrigger]); // Add refreshTrigger to refetch when document is updated

  // Effect to refresh annotations when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && documentId) {
      fetchAnnotations();
    }
  }, [refreshTrigger, documentId]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Escape key to dismiss completion modal
      if (event.key === 'Escape' && showCompletionModal) {
        setShowCompletionModal(false);
        return;
      }
      
      // Ctrl+Enter to finalize document when signatures are applied
      if (event.ctrlKey && event.key === 'Enter' && appliedSignatures.length > 0 && !loading) {
        event.preventDefault();
        finalizeDocument();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCompletionModal, appliedSignatures.length, loading]);
  
  // Basic document event handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);

    // Auto-scale the PDF to fit the container, mirroring SignatureFieldPlacement behaviour
    if (!initialScaleSet && documentRef.current) {
      setTimeout(() => {
        const container = documentRef.current;
        const containerWidth = container.clientWidth - 100; // Leave a little room for padding
        const pageWidth = 800; // Same base width used in Page `width` prop
        const calculatedScale = Math.min(1.0, containerWidth / pageWidth);
        setScale(calculatedScale);
        setInitialScaleSet(true);
      }, 100);
    }
  };

  // Add handler to track PDF page dimensions
  const onPageLoadSuccess = (page) => {
    const viewport = page.getViewport({ scale: 1 });
    setPdfPageDimensions({
      width: viewport.width,
      height: viewport.height
    });
    
    // CRITICAL FIX: Track rendered dimensions synchronously without setTimeout
    // This prevents fallback to incorrect 800px assumption during coordinate calculations
    const pageEl = documentRef.current?.querySelector('.react-pdf__Page');
    if (pageEl) {
      const rect = pageEl.getBoundingClientRect();
      setRenderedPageDimensions({
        width: rect.width / scale, // Get unscaled width
        height: rect.height / scale
      });
    }
    
    // Also update after a small delay to catch any DOM reflows
    setTimeout(() => {
      const pageEl = documentRef.current?.querySelector('.react-pdf__Page');
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        setRenderedPageDimensions({
          width: rect.width / scale,
          height: rect.height / scale
        });
      }
    }, 50);
  };

  const previousPage = () => {
    if (viewMode === 'continuous') {
      // In continuous mode, smooth scroll to previous page
      const targetPage = Math.max(pageNumber - 1, 1);
      scrollToPage(targetPage);
    } else {
      setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
    }
  };

  const nextPage = () => {
    if (viewMode === 'continuous') {
      // In continuous mode, smooth scroll to next page
      const targetPage = Math.min(pageNumber + 1, numPages);
      scrollToPage(targetPage);
    } else {
      setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages));
    }
  };
  
  // Scroll to a specific page in continuous mode
  const scrollToPage = useCallback((targetPage) => {
    if (viewMode === 'continuous' && pageRefs.current[targetPage]) {
      const element = pageRefs.current[targetPage];
      const container = scrollContainerRef.current || documentRef.current;
      
      if (container && element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = container.scrollTop + elementRect.top - containerRect.top - 20; // 20px offset for better visibility
        
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    } else {
      setPageNumber(targetPage);
    }
  }, [viewMode]);

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3));
  };
  
  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  // CRITICAL FIX: Update rendered dimensions when scale changes
  // This ensures coordinate calculations are accurate at any zoom level
  useEffect(() => {
    if (!pdfPageDimensions) return;
    
    const updateRenderedDimensions = () => {
      const pageEl = documentRef.current?.querySelector('.react-pdf__Page');
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        setRenderedPageDimensions({
          width: rect.width / scale,
          height: rect.height / scale
        });
        console.log(`📏 Updated rendered dimensions for scale ${scale.toFixed(2)}: ${rect.width}px (base: ${(rect.width/scale).toFixed(0)}px)`);
      }
    };
    
    // Small delay to let DOM update after scale change
    const timer = setTimeout(updateRenderedDimensions, 100);
    return () => clearTimeout(timer);
  }, [scale, pdfPageDimensions]);

  // Check if user can sign the document
  const canUserSign = () => {
    // Check if we have a signature token in the URL (means user was invited to sign)
    const params = new URLSearchParams(window.location.search);
    const hasSignatureToken = !!params.get('token');
    
    return isOwner || hasSignatureRequest || sharePermission === "sign" || canSign || hasSignatureToken;
  };
  
  // Check if user can add signatures freely without needing signature requests
  const canAddSignatureAnytime = () => {
    // Check if we have a signature token in the URL (means user was invited to sign)
    const params = new URLSearchParams(window.location.search);
    const hasSignatureToken = !!params.get('token');
    
    return isOwner || sharePermission === "sign" || canSign || hasSignatureToken;
  };

  // Signature-related functions
  const handleFieldClick = async (field, isEditMode = false) => {
    console.log('handleFieldClick called with field:', field, 'isEditMode:', isEditMode);
    
    // In review mode, handle presigning differently
    if (isReviewMode && onFieldPresign) {
      const isCurrentUserField = field.userId === user?.id || 
                                field.userEmail === user?.email ||
                                field.assignedTo?.email === user?.email ||
                                field.assignedTo?.isCurrentUser;
      
      if (isCurrentUserField && !presignedFields.has(field.id)) {
        onFieldPresign(field.id);
        return;
      }
    }
    
    if (!canUserSign() && !canAddSignatureAnytime()) return;

    // If this is an edit operation, skip default signature check and go straight to signature creation
    if (isEditMode) {
      console.log('Edit mode - skipping default signature check');
      setShowSignaturePlacement(true);
      setSelectedSignatureField(field);
      console.log('Set selectedSignatureField to:', field);
      return; // CRITICAL: Prevents modal from opening
    }

    try {
      // Check if user has a default signature (only for new signatures, not edits)
      const response = await fetch(`${API_URL}/users/default-signature`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('Default signature API response:', data);
      if (response.ok && data.defaultSignature) {
        // User has a default signature - apply it directly
        const defaultSig = data.defaultSignature;
        const signatureData = {
          signatureData: defaultSig.data,
          signatureType: defaultSig.type,
          fieldId: field.id,
          position: {
            x: field.x !== undefined ? field.x : 100,
            y: field.y !== undefined ? field.y : 100,
            width: field.width || 300,
            height: field.height || 100
          },
          page: field.page || pageNumber,
          userId: field.userId,
          userName: field.userName,
          isAppliedButNotFinalized: true // Flag to indicate this needs finalization
        };
        handleSignatureAdded(signatureData);
        console.log('Applied default signature directly to field:', field.id);
        return; // CRITICAL: Prevents modal from opening
      }
    } catch (error) {
      console.error('Error checking for default signature:', error);
    }

    // No default signature found - proceed with normal flow
    setShowSignaturePlacement(true);
    setSelectedSignatureField(field);
    console.log('Set selectedSignatureField to:', field);
  };
  
  const handleSignatureAdded = (signatureData) => {
    console.log('handleSignatureAdded called with:', signatureData);
    console.log('selectedSignatureField:', selectedSignatureField);
    console.log('PDF dimensions:', pdfPageDimensions);
    console.log('Rendered dimensions:', renderedPageDimensions);
    
    let newSignature = null;
    let newlySignedFieldIds = [];
    
    // Calculate normalized coordinates
    const calculateNormalizedCoordinates = (position, width, height) => {
      if (!pdfPageDimensions) {
        console.warn('PDF dimensions not available, using legacy format');
        return null;
      }
      
      // CRITICAL FIX: Get ACTUAL rendered width from DOM, accounting for current scale
      const pageElement = documentRef.current?.querySelector('.react-pdf__Page');
      const actualRenderedWidth = pageElement ? pageElement.getBoundingClientRect().width : (window.innerWidth > 1024 ? 800 * scale : 800);
      const renderedWidth = actualRenderedWidth;
      const scaleFactor = pdfPageDimensions.width / renderedWidth;
      
      // Convert pixel coordinates to PDF points
      const pdfX = position.x * scaleFactor;
      const pdfY = position.y * scaleFactor;
      const pdfWidth = (width || 300) * scaleFactor;
      const pdfHeight = (height || 100) * scaleFactor;
      
      // Also calculate normalized (0-1) coordinates
      const normalizedX = pdfX / pdfPageDimensions.width;
      const normalizedY = pdfY / pdfPageDimensions.height;
      const normalizedWidth = pdfWidth / pdfPageDimensions.width;
      const normalizedHeight = pdfHeight / pdfPageDimensions.height;
      
      console.log(`📐 Coordinate Conversion Summary:`);
      console.log(`   Scale: ${scale.toFixed(2)}x`);
      console.log(`   Rendered Width: ${renderedWidth.toFixed(0)}px`);
      console.log(`   PDF Width: ${pdfPageDimensions.width.toFixed(0)}pts`);
      console.log(`   Scale Factor: ${scaleFactor.toFixed(4)}`);
      console.log(`   Pixel → PDF: (${position.x}, ${position.y}) → (${pdfX.toFixed(2)}, ${pdfY.toFixed(2)})`);
      console.log(`   Normalized: (${normalizedX.toFixed(4)}, ${normalizedY.toFixed(4)})`);
      
      return {
        // Store multiple formats for maximum compatibility
        x: pdfX, // PDF points
        y: pdfY,
        width: pdfWidth,
        height: pdfHeight,
        // Normalized coordinates (0-1 range) - MOST RELIABLE
        normalizedX,
        normalizedY,
        normalizedWidth,
        normalizedHeight,
        // Original pixel coordinates for reference
        pixelX: position.x,
        pixelY: position.y,
        pixelWidth: width || 300,
        pixelHeight: height || 100,
        // Metadata for debugging and verification
        renderedPageWidth: renderedWidth,
        pdfPageWidth: pdfPageDimensions.width,
        pdfPageHeight: pdfPageDimensions.height,
        scale: scale, // Current scale when coordinates were saved
        scaleFactor: scaleFactor,
        coordinateFormat: 'v2' // Version 2 with normalized coordinates
      };
    };
    
    // FIX: Only apply to the specific field if fieldId is present
    if (signatureData.fieldId) {
      // Find the field by id
      const field = signatureFields.find(f => f.id === signatureData.fieldId);
      const coordinates = field
        ? calculateNormalizedCoordinates(
            { x: field.x, y: field.y },
            field.width,
            field.height
          ) || {
            x: field.x,
            y: field.y,
            width: field.width || 300,
            height: field.height || 100
          }
        : signatureData.position;
      newSignature = {
        id: `signature-${signatureData.fieldId || Date.now()}`,
        page: field?.page || signatureData.page || pageNumber,
        position: coordinates,
        data: signatureData.signatureData,
        type: signatureData.signatureType,
        fieldId: signatureData.fieldId,
        userId: field?.userId || signatureData.userId,
        userName: field?.userName || signatureData.userName
      };
      setAppliedSignatures(prev => {
        // Remove any existing signature for this field first (for editing)
        const filteredSignatures = prev.filter(sig => sig.fieldId !== signatureData.fieldId);
        const updated = [...filteredSignatures, newSignature];
        console.log('Updated appliedSignatures with new signature (replaced existing):', updated);
        return updated;
      });
      newlySignedFieldIds = [signatureData.fieldId];
    } else if (selectedSignatureField) {
      // (Legacy fallback, should not be hit, but keep for safety)
      const coordinates = calculateNormalizedCoordinates(
        { x: selectedSignatureField.x, y: selectedSignatureField.y },
        selectedSignatureField.width,
        selectedSignatureField.height
      ) || {
        x: selectedSignatureField.x,
        y: selectedSignatureField.y,
        width: selectedSignatureField.width || 300,
        height: selectedSignatureField.height || 100
      };
      newSignature = {
        id: `signature-${selectedSignatureField.id || Date.now()}`,
        page: selectedSignatureField.page || pageNumber,
        position: coordinates,
        data: signatureData.signatureData,
        type: signatureData.signatureType,
        fieldId: selectedSignatureField.id,
        userId: selectedSignatureField.userId,
        userName: selectedSignatureField.userName
      };
      setAppliedSignatures(prev => {
        const filteredSignatures = prev.filter(sig => sig.fieldId !== selectedSignatureField.id);
        const updated = [...filteredSignatures, newSignature];
        return updated;
      });
      newlySignedFieldIds = [selectedSignatureField.id];
    } else if (canAddSignatureAnytime()) {
      // Free placement - only for owners
      const coordinates = calculateNormalizedCoordinates(
        signatureData.position,
        signatureData.position.width,
        signatureData.position.height
      ) || signatureData.position;
      newSignature = {
        id: `signature-${Date.now()}`,
        page: signatureData.page || pageNumber,
        position: coordinates,
        data: signatureData.signatureData,
        type: signatureData.signatureType,
        userId: user?.id,
        userName: user?.name
      };
      setAppliedSignatures(prev => [...prev, newSignature]);
    }
    
    setShowSignaturePlacement(false);
    setSelectedSignatureField(null);
    
    // Check if all required fields are now completed (signatures + form fields)
    const totalRequiredSignatureFields = signatureFields.filter(field => field.userId === user?.id).length;
    const currentSignedFields = appliedSignatures.length + (newSignature ? 1 : newlySignedFieldIds.length);
    
    // Count completed form fields assigned to current user
    const currentUserFormFields = [];
    if (existingFormFields && existingFormFields.formFields) {
      existingFormFields.formFields.forEach(formField => {
        if (formField.userEmail?.toLowerCase() === user?.email?.toLowerCase()) {
          currentUserFormFields.push(formField);
        }
      });
    }
    const totalRequiredFormFields = currentUserFormFields.length;
    const currentFilledFormFields = currentUserFormFields.filter(field => 
      field.fieldValue && field.fieldValue.trim() !== ''
    ).length;
    
    const allSignatureFieldsSigned = hasSignatureRequest && totalRequiredSignatureFields > 0 && currentSignedFields >= totalRequiredSignatureFields;
    const allFormFieldsCompleted = totalRequiredFormFields === 0 || currentFilledFormFields >= totalRequiredFormFields;
    const allFieldsSigned = allSignatureFieldsSigned && allFormFieldsCompleted;
    
    setJustSignedFields(newlySignedFieldIds);
    setAllRequiredFieldsSigned(allFieldsSigned);
    
    // Show completion modal instead of just success message
    // setShowCompletionModal(true);
    
    // 🔄 Cache invalidation not needed here as signatures are only locally applied, not saved to backend yet
  };

  // Removed unused isDocumentSignable function and old isFieldSigned function
  // (replaced with enhanced version that uses unified signature state)
  
  const finalizeDocument = async () => {
    if (!canUserSign() && !canAddSignatureAnytime()) return;
    
    // Store applied signatures for backend call before clearing them (declare outside try/catch)
    let signaturesForBackend = [];
    
    try {
      if (!document?.id) {
        setError('Document not found or not fully loaded');
        return;
      }
      
      console.log('DEBUG: Starting finalization process...');
      console.log('DEBUG: appliedSignatures to finalize:', appliedSignatures);
      
      // Get signature token from URL for API calls
      const params = new URLSearchParams(window.location.search);
      const signatureToken = params.get('token');
      
      setLoading(true);
      
      // Store applied signatures for backend call before clearing them
      signaturesForBackend = [...appliedSignatures];
      
      // CRITICAL FIX: Clear applied signatures IMMEDIATELY to prevent duplicate rendering
      setAppliedSignatures([]);
      
      // Format signatures for the backend
      const formattedSignatures = signaturesForBackend.map(sig => {
        // Ensure we have the required fields
        if (!sig.type || !sig.data) {
          throw new Error('Each signature must have type and data');
        }

        // If this is a field signature, use the field's coordinates
        if (sig.fieldId) {
          const field = signatureFields.find(f => f.id === sig.fieldId);
          if (field) {
            console.log(`Formatting signature for field ${sig.fieldId}:`, {
              fieldData: { x: field.x, y: field.y, width: field.width, height: field.height },
              signaturePosition: sig.position
            });
            
            // Check if we have v2 coordinates with normalization
            const coordinates = sig.position?.coordinateFormat === 'v2' ? {
              page: field.page || 1,
              x: sig.position.x,
              y: sig.position.y,
              width: sig.position.width,
              height: sig.position.height,
              // Include all coordinate formats
              normalizedX: sig.position.normalizedX,
              normalizedY: sig.position.normalizedY,
              normalizedWidth: sig.position.normalizedWidth,
              normalizedHeight: sig.position.normalizedHeight,
              pixelX: sig.position.pixelX,
              pixelY: sig.position.pixelY,
              renderedPageWidth: sig.position.renderedPageWidth,
              pdfPageWidth: sig.position.pdfPageWidth,
              pdfPageHeight: sig.position.pdfPageHeight,
              coordinateFormat: sig.position.coordinateFormat
            } : {
              page: field.page || 1,
              x: field.x || 100,
              y: field.y || 100,
              width: field.width || 300,
              height: field.height || 100,
              coordinateFormat: 'v1' // Legacy format
            };
            
            return {
              type: sig.type,
              data: sig.data,
              coordinates,
              fieldId: sig.fieldId,
              userId: sig.userId
            };
          }
        }
        
        // For free-floating signatures, use the position from the signature
        const coordinates = sig.position?.coordinateFormat === 'v2' ? sig.position : {
          page: sig.page || 1,
          x: sig.position?.x || 100,
          y: sig.position?.y || 100,
          width: sig.position?.width || 300,
          height: sig.position?.height || 100,
          coordinateFormat: 'v1'
        };
        
        return {
          type: sig.type,
          data: sig.data,
          coordinates,
          fieldId: sig.fieldId,
          userId: sig.userId
        };
      });
      
      await signatureService.signDocument(
        document.id,
        formattedSignatures,
        signatureRequestId,
        signatureToken
      );
      
      console.log('✍️ DEBUG: signDocument call completed successfully');
      setSuccessMessage('Document signed successfully!');
      setIsDocumentFinalized(true); // Mark document as finalized so form fields turn green
      
      // ✅ CACHE INVALIDATION: Already handled in signatureService, but add extra invalidation for safety
      invalidateDocumentQueries();
      invalidateSignatureQueries(documentId);
      console.log('🔄 Cache invalidated after finalization in DocumentViewer');
      
      // 🚀 Smart cache invalidation using document cache service
      documentCacheService.onSignatureAdded(documentId, {
        signatureCount: formattedSignatures.length,
        completesDocument: true // Assume completion for now - could be determined by response
      });
      
      // Refresh document and signatures
      const updatedDoc = await documentService.getDocument(documentId, signatureToken);
      setDocument(updatedDoc);
      
      // Load updated signatures with improved synchronization
      console.log('DEBUG: Reloading signature data after finalization...');
      const signatureData = await signatureService.getDocumentSignatures(documentId, signatureToken);
      console.log('Loaded signatures after finalization:', signatureData);
      
      let signatures = [];
      let allSignatureFields = [];
      
      if (signatureData?.signatures) {
        signatures = signatureData.signatures;
        console.log('Individual signatures loaded:', signatures.map(sig => ({
          id: sig.id,
          fieldId: sig.fieldId,
          userId: sig.userId,
          coordinates: sig.coordinates,
          x: sig.x,
          y: sig.y
        })));
        setExistingSignatures({ signatures });
        console.log('Set existingSignatures to:', signatures);
        console.log('DEBUG: First signature in existingSignatures:', signatures[0]);
      }
      
      // Also reload signature fields with updated signedAt data
      if (signatureData?.requests?.length > 0) {
        allSignatureFields = signatureData.requests.flatMap(req => {
          if (req.signatureFields) {
            return req.signatureFields.map(field => ({
              ...field,
              userId: req.userId,
              userName: req.userName,
              userEmail: req.userEmail
            }));
          }
          return [];
        });
        
        console.log('DEBUG: Reloaded signature fields after finalization:', allSignatureFields);
        console.log('DEBUG: Fields with signedAt after finalization:', allSignatureFields.filter(f => f.signedAt));
        console.log('DEBUG: Fields with signedIP after finalization:', allSignatureFields.filter(f => f.signedIP));
        setSignatureFields(allSignatureFields);
      }
      
      // Clear applied signatures and update unified state
      setAppliedSignatures([]);
      updateSignatureState(signatures, allSignatureFields, []);
      console.log('DEBUG: Signature state synchronized after finalization');
      
      setHasSignatureRequest(false);
      
      setLoading(false);

      // Redirect to confirmation page where user can only download the signed doc
      if (signatureToken) {
        navigate(`/signature-sent/${document.id}?token=${encodeURIComponent(signatureToken)}`);
      } else {
        navigate(`/signature-sent/${document.id}`);
      }
    } catch (err) {
      console.error('Error signing document:', err);
      setError('Failed to sign document. Please try again.');
      // Restore applied signatures on error
      if (signaturesForBackend) {
        setAppliedSignatures(signaturesForBackend);
      }
      setLoading(false);
    }
  };
  
  // Render all signatures
  const renderAllSignatures = () => {
    const renderedElements = [];
    
    // Only log in development when debug is enabled
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
      console.log('renderAllSignatures called. appliedSignatures:', appliedSignatures);
      console.log('signatureFields:', signatureFields);
      console.log('pageNumber:', pageNumber);
      console.log('existingSignatures:', existingSignatures);
    }
    
    // In continuous mode, render signatures for all visible pages
    if (viewMode === 'continuous') {
      const pagesToRender = Array.from(visiblePages).length > 0 ? Array.from(visiblePages) : [pageNumber];
      
      pagesToRender.forEach(currentPageNum => {
        const pageElement = pageRefs.current[currentPageNum]?.querySelector('.react-pdf__Page__canvas');
        if (!pageElement) return;
        
        const elements = renderSignaturesForPage(currentPageNum, pageElement);
        renderedElements.push(...elements);
      });
      
      return renderedElements;
    }
    
    // Get the actual PDF page element for relative positioning (paged mode)
    const pageElement = documentRef.current?.querySelector('.react-pdf__Page');
    if (!pageElement) {
      console.log('PDF page element not found, skipping signature rendering');
      return [];
    }
    
    return renderSignaturesForPage(pageNumber, pageElement);
  };
  
  // Helper function to render signatures for a specific page
  const renderSignaturesForPage = (targetPageNumber, pageElement) => {
    const renderedElements = [];
    
    // Get page dimensions for coordinate conversion
    const pageRect = pageElement.getBoundingClientRect();
    
    // In continuous mode, positions are relative to the page container, not document
    let pageOffsetX = 0;
    let pageOffsetY = 0;
    
    if (viewMode !== 'continuous') {
      // In paged mode, calculate relative to document container
      const containerRect = documentRef.current?.getBoundingClientRect();
      pageOffsetX = pageRect.left - containerRect.left;
      pageOffsetY = pageRect.top - containerRect.top;
    }
    
    console.log('Page positioning data:', {
      pageRect: { width: pageRect.width, height: pageRect.height, left: pageRect.left, top: pageRect.top },
      pageOffset: { x: pageOffsetX, y: pageOffsetY },
      scale: scale,
      viewMode: viewMode
    });
    
    // Use unified signature state for better tracking and deduplication
    const { existingSignatures: unifiedSignatures, signatureFields: unifiedFields, fieldToSignatureMap } = signatureState;
    console.log('DEBUG: Using unified signature state:', {
      signatures: unifiedSignatures.length,
      fields: unifiedFields.length,
      mappedFields: fieldToSignatureMap.size,
      isLoading: signatureState.isLoading
    });
    
    // Track which fields have actual signatures (enhanced tracking)
    const renderedFieldIds = new Set();
    const renderedSignatureIds = new Set(); // Additional tracking for signature objects
    
    // PHASE 1: Render actual signatures from database (enhanced with unified state)
    if (unifiedSignatures && unifiedSignatures.length > 0) {
      const signaturesForCurrentPage = unifiedSignatures.filter(signature => {
        const sigPage = parseInt(signature.page || signature.coordinates?.page || 1);
        return sigPage === targetPageNumber;
      });
      
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
        console.log(`=== PHASE 1: ACTUAL SIGNATURES (ENHANCED) ===`);
        console.log(`Found ${signaturesForCurrentPage.length} signatures for page ${targetPageNumber}`);
        console.log(`Fieldmap has ${fieldToSignatureMap.size} entries:`, Array.from(fieldToSignatureMap.keys()));
      }
      
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
        signaturesForCurrentPage.forEach((signature, index) => {
          console.log(`PHASE 1 - Signature ${index + 1}:`, {
            id: signature.id,
            fieldId: signature.fieldId,
            userId: signature.userId,
            coordinates: signature.coordinates,
            isFieldSignedMarker: signature.isFieldSignedMarker
          });
        });
      }
      
      signaturesForCurrentPage.forEach((signature) => {
        // Skip field-signed markers that don't have actual image data
        if (signature.isFieldSignedMarker) {
          console.log(`DEBUG: PHASE 1 - Skipping field-signed marker for field ${signature.fieldId}`);
          if (signature.fieldId) {
            renderedFieldIds.add(String(signature.fieldId));
          }
          return;
        }
        
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
          console.log(`DEBUG: PHASE 1 - Rendering actual signature from database:`, signature.id);
          console.log(`DEBUG: PHASE 1 - Signature fieldId:`, signature.fieldId);
        }
        
        // Parse coordinates from signature (handle both formats like PDF embedding does)
        let coordinates = null;
        if (signature.coordinates) {
          try {
            if (signature.coordinates === '[object Object]') {
              console.log(`⚠️ Legacy [object Object] coordinates for signature ${signature.id}`);
              coordinates = { page: 1, x: 100, y: 100, width: 300, height: 100 };
            } else {
              coordinates = typeof signature.coordinates === 'string' 
                ? JSON.parse(signature.coordinates) 
                : signature.coordinates;
            }
          } catch (parseError) {
            console.log(`⚠️ Failed to parse coordinates for signature ${signature.id}: ${signature.coordinates}`);
            coordinates = { page: 1, x: 100, y: 100, width: 300, height: 100 };
          }
        }
        
        // Use coordinates from parsed data (same as PDF embedding logic)
        const x = coordinates?.x || signature.x || 100;
        const y = coordinates?.y || signature.y || 100;  
        const width = coordinates?.width || signature.width || 300;
        const height = coordinates?.height || signature.height || 100;
        
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_SIGNATURES === 'true') {
          console.log(`DEBUG: PHASE 1 - Signature ${signature.id} coordinates:`, { x, y, width, height });
        }
        
        // CRITICAL FIX: Use original dimensions without adjustment to match PDF embedding exactly
        // Removed aspect ratio adjustment that was causing size mismatch between viewer and PDF
        
        // Convert to absolute positioning using page-relative coordinates
        const position = getAbsolutePosition(x, y, width, height);
        
        console.log(`DEBUG: PHASE 1 - Signature ${signature.id} absolute position:`, position);
        
        // Enhanced tracking with both field and signature IDs
        if (signature.fieldId) {
          const fieldIdStr = String(signature.fieldId);
          console.log(`DEBUG: PHASE 1 - Adding fieldId ${fieldIdStr} to renderedFieldIds`);
          renderedFieldIds.add(fieldIdStr);
        }
        renderedSignatureIds.add(signature.id);
        
        console.log(`DEBUG: PHASE 1 - Enhanced tracking:`, {
          renderedFieldIds: Array.from(renderedFieldIds),
          renderedSignatureIds: Array.from(renderedSignatureIds)
        });
        
        // Render signature with PDF-style formatting (matching PDF embedding exactly)
        // Add defensive key generation to prevent React duplicate key warnings
        const signatureKey = `actual-signature-${signature.id}-${targetPageNumber}-${signature.fieldId || 'free'}`;
        
        // ALIGNMENT FIX: Adjust Y position to match embedding service
        // The embedding service positions the signature box 10 pixels UP from the base Y coordinate
        // (see signatureEmbedService.js line 181: boxY = y + 10)
        // To match this in CSS coordinates, we subtract 10 from the top position
        const adjustedTop = parseFloat(position.top) - 10;
        
        renderedElements.push(
          <div
            key={signatureKey}
            className="absolute"
            style={{
              left: position.left,
              top: `${adjustedTop}px`,
              width: position.width,
              height: position.height, // Use exact same height as placeholder fields
              zIndex: 50,
              position: 'absolute'
            }}
          >
            {/* Signature box */}
            <div className="border-2 border-green-500 rounded-xl shadow-sm overflow-hidden"
                 style={{ 
                   width: '100%', 
                   height: '100%' // Leave room for text below
                 }}>
              {/* Signature content - full height */}
              <div className="relative w-full h-full">
                {/* Signature image area - uses full height */}
                <div className="w-full h-full flex items-center justify-center p-1">
                  <img
                    src={signature.signatureUrl || (() => {
                      // Generate signature URL with proper authentication
                      const params = new URLSearchParams(window.location.search);
                      const signatureToken = params.get('token');
                      return signatureService.getSignatureImageUrl(signature.id, signatureToken);
                    })()}
                    alt="Signature"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      console.error('Error loading signature image:', e);
                      e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                      e.target.alt = 'Signature unavailable';
                      e.target.style.background = '#f0f0f0';
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* ID text below the box */}
            <div className="text-xs text-green-700 font-medium truncate bg-green-100 px-2 py-1 rounded"
                 style={{ fontSize: '10px', marginTop: '2px' }}>
              SIGNED: {signature.id ? signature.id.substring(0, 23).toUpperCase() : 'UNKNOWN'}
            </div>
          </div>
        );
      });
    }
    
    
    // PHASE 2: Render signature fields ONLY as placeholders where no actual signature exists (ENHANCED)
    if (unifiedFields && unifiedFields.length > 0) {
      const fieldsForCurrentPage = unifiedFields.filter(field => 
        field.page === targetPageNumber || (field.page === undefined && targetPageNumber === 1)
      );
      
      console.log(`=== PHASE 2: SIGNATURE FIELDS (ENHANCED) ===`);
      console.log(`Found ${fieldsForCurrentPage.length} fields for page ${targetPageNumber}`);
      console.log(`Current renderedFieldIds:`, Array.from(renderedFieldIds));
      console.log(`FieldToSignatureMap:`, Array.from(fieldToSignatureMap.keys()));
      
      fieldsForCurrentPage.forEach((field, index) => {
        console.log(`PHASE 2 - Field ${index + 1}:`, {
          id: field.id,
          userId: field.userId,
          page: field.page,
          x: field.x,
          y: field.y,
          signedAt: field.signedAt,
          hasMappedSignature: fieldToSignatureMap.has(String(field.id))
        });
      });
      
      fieldsForCurrentPage.forEach(field => {
        const fieldIdStr = String(field.id);
        
        // ENHANCED DEDUPLICATION LOGIC
        // 1. Skip if this field already has an actual signature rendered in Phase 1
        if (renderedFieldIds.has(fieldIdStr)) {
          console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${fieldIdStr} - already rendered in Phase 1`);
          return;
        }
        
        // 2. Skip if the unified field-to-signature map shows this field is signed
        if (fieldToSignatureMap.has(fieldIdStr)) {
          console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${fieldIdStr} - found in unified signature map`);
          return;
        }
        
        // 3. ADDITIONAL SAFEGUARD: Check using the improved isFieldSigned function
        if (isFieldSigned(field.id)) {
          console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${fieldIdStr} - isFieldSigned returned true`);
          return;
        }
        
        // 4. LEGACY COMPATIBILITY: Also check against existing signatures state for backward compatibility
        const hasLegacySignature = existingSignatures?.signatures?.some(sig => 
          String(sig.fieldId) === fieldIdStr
        );
        if (hasLegacySignature) {
          console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${fieldIdStr} - found in legacy existingSignatures`);
          return;
        }
        
        // 5. Check if field has signedAt data (for multi-user scenarios)
        if (field.signedAt) {
          console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${fieldIdStr} - field has signedAt: ${field.signedAt}`);
          return;
        }
        
        console.log(`DEBUG: PHASE 2 - ⚠️ RENDERING field ${fieldIdStr} - passed all deduplication checks`);
        
        // Use precise coordinates without fallbacks
        const x = field.x !== undefined ? field.x : 100;
        const y = field.y !== undefined ? field.y : 100;
        const width = field.width || 300;
        const height = field.height || 100;
        
        // Convert to absolute positioning
        const position = getAbsolutePosition(x, y, width, height);
        
        // Check if this field has a signature applied to it (temporary ones)
        // Skip applied signatures during loading to prevent duplicates during finalization
        // CRITICAL FIX: Only show applied signatures for fields belonging to the current user
        const appliedSignature = !loading && field.userId === user?.id ? appliedSignatures.find(sig => sig.fieldId === field.id) : null;
        
        
        if (appliedSignature) {
          console.log(`DEBUG: PHASE 2 - Field ${field.id} has appliedSignature, rendering applied signature`);
          // Show applied signature (just signed, not finalized)
          // Add defensive key generation
          const appliedKey = `applied-${field.id || `field-${Math.random()}`}-${targetPageNumber}-applied`;
          renderedElements.push(
            <div
              key={appliedKey}
              className="absolute"
              style={{
                left: position.left,
                top: position.top,
                width: position.width,
                height: position.height, // Use exact same height as placeholder fields
                zIndex: 50,
                position: 'absolute'
              }}
            >
              {/* Signature box */}
              <div className="border-2 border-blue-500 rounded-xl shadow-sm overflow-hidden cursor-pointer"
                   style={{ 
                     width: '100%', 
                     height: '100%' // Leave room for text below
                   }}
                   title="Click to edit signature"
                   onClick={e => {
                     setEditingSignatureField(field); // Track which field is being edited
                     handleFieldClick(field, true); // Pass isEditMode = true to skip default signature
                   }}>
                {/* Signature content area - full height */}
                <div className="relative w-full h-full">
                  {/* Signature image area - uses full height */}
                  <div className="w-full h-full flex items-center justify-center p-1">
                    <img
                      src={appliedSignature.data}
                      alt="Applied Signature"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        console.error('Error loading applied signature image:', e);
                        e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                        e.target.alt = 'Signature unavailable';
                        e.target.style.background = '#f0f0f0';
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* ID text below the box */}
              <div className="text-xs text-blue-700 font-medium truncate bg-blue-100 px-2 rounded cursor-pointer"
                   style={{ fontSize: '10px', marginTop: '2px' }}
                   onClick={e => {
                     e.stopPropagation(); // Prevent event bubbling
                     setEditingSignatureField(field); // Track which field is being edited
                     handleFieldClick(field, true); // Pass isEditMode = true to skip default signature
                   }}>
                CLICK TO EDIT
              </div>
            </div>
          );
        } else {
          console.log(`DEBUG: PHASE 2 - Field ${field.id} has NO appliedSignature, checking if it should show placeholder`);
          // Show signature field placeholder for any user who needs to sign
          const isCurrentUserField = field.userId === user?.id;
          
          // Check if accessing via signature token and field is for this signer
          const params = new URLSearchParams(window.location.search);
          const signatureToken = params.get('token');
          let isSignatureTokenUser = false;
          
          if (signatureToken) {
            // Prefer signer email returned by backend (no need to decode token)
            const backendSignerEmail = document?.signatureAuth?.signerEmail;
            if (backendSignerEmail) {
              isSignatureTokenUser = field.userEmail === backendSignerEmail;
              console.log('DEBUG: PHASE 2 - Token field matching via backend signerEmail:', {
                fieldUserEmail: field.userEmail,
                backendSignerEmail,
                isMatch: isSignatureTokenUser
              });
            } else if (signatureToken.includes('.') && signatureToken.split('.').length >= 2) {
              try {
                // Fallback for legacy JWT tokens – decode payload
                const tokenPayload = JSON.parse(atob(signatureToken.split('.')[1]));
                isSignatureTokenUser = field.userEmail === tokenPayload.signer_email;
                console.log('DEBUG: PHASE 2 - Token field matching via decoded JWT:', {
                  fieldUserEmail: field.userEmail,
                  tokenSignerEmail: tokenPayload.signer_email,
                  isMatch: isSignatureTokenUser
                });
              } catch (e) {
                console.warn('Could not decode signature token for field matching:', e);
              }
            }
          }
          
          const isAssignedToCurrentUser = (hasSignatureRequest && isCurrentUserField) || isSignatureTokenUser;
          const isFieldAlreadySigned = field.signedAt !== null && field.signedAt !== undefined;
          
          console.log('DEBUG: PHASE 2 - Rendering placeholder field:', {
            id: field.id,
            signedAt: field.signedAt,
            isFieldAlreadySigned,
            isCurrentUserField,
            hasSignatureRequest,
            isSignatureTokenUser,
            isAssignedToCurrentUser,
            fieldData: field
          });
          
          // DO NOT render placeholder if field is already signed
          if (isFieldAlreadySigned) {
            console.log(`DEBUG: PHASE 2 - ✅ SKIPPING field ${field.id} - field is already signed (signedAt: ${field.signedAt})`);
            return;
          }
          
          console.log(`DEBUG: PHASE 2 - ⚠️ RENDERING placeholder for field ${field.id}`);
          
          // Add defensive key generation for placeholders
          const placeholderKey = `field-placeholder-${field.id || Math.random()}-${targetPageNumber}-${field.userId || 'unknown'}`;
          
          // Calculate field dimensions for progressive layout (same as SignatureFieldPlacement)
          const fieldWidth = parseFloat(position.width) || 200;
          const fieldHeight = parseFloat(position.height) || 50;
          
          // Determine colors based on field status (original color scheme)
          const getBorderColor = () => {
            if (isFieldAlreadySigned) return 'border-green-500';
            if (presignedFields.has(field.id)) return 'border-green-400';
            if (isAssignedToCurrentUser) return 'border-blue-500';
            return 'border-gray-400';
          };
          
          const getBgColor = () => {
            if (isFieldAlreadySigned) return 'bg-green-50';
            if (presignedFields.has(field.id)) return 'bg-green-100';
            if (isAssignedToCurrentUser) return 'bg-blue-50';
            return 'bg-gray-100';
          };
          
          const getTextColor = () => {
            if (isFieldAlreadySigned) return 'text-green-700';
            if (presignedFields.has(field.id)) return 'text-green-700';
            if (isAssignedToCurrentUser) return 'text-blue-700';
            return 'text-gray-600';
          };
          
          const getIconColor = () => {
            if (isFieldAlreadySigned) return 'text-green-600';
            if (presignedFields.has(field.id)) return 'text-green-600';
            if (isAssignedToCurrentUser) return 'text-blue-600';
            return 'text-gray-600';
          };
          
          renderedElements.push(
            <div
              key={placeholderKey}
              data-field-id={field.id}
              className={`absolute border-2 ${getBorderColor()} ${getBgColor()} rounded-xl shadow-sm flex flex-col select-none user-select-none ${
                isAssignedToCurrentUser && !isFieldAlreadySigned && !presignedFields.has(field.id) 
                  ? 'cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105' 
                  : !isAssignedToCurrentUser && !isFieldAlreadySigned
                    ? 'cursor-not-allowed opacity-60'
                    : ''
              }`}
              style={{
                left: position.left,
                top: position.top,
                width: position.width,
                height: position.height,
                zIndex: 50
              }}
              onClick={() => {
                if (isAssignedToCurrentUser && !isFieldAlreadySigned && !presignedFields.has(field.id)) {
                  handleFieldClick(field);
                } else if (!isAssignedToCurrentUser && !isFieldAlreadySigned) {
                  alert(`This signature field is assigned to ${field.userEmail || 'another user'}. You cannot sign it.`);
                }
              }}
            >
              <div className="flex flex-col h-full p-2 text-xs pointer-events-none">
                {/* Progressive layout based on field dimensions (same as SignatureFieldPlacement) */}
                {fieldHeight >= 70 ? (
                  // Tall field: Show icon + label at top, content below
                  <>
                    {/* Top section with signature icon and label */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-1">
                        <Pen className={`h-3 w-3 ${getIconColor()}`} />
                        <span className={`font-medium text-xs ${getTextColor()}`}>
                          Signature
                        </span>
                      </div>
                    </div>
                    
                    {/* Center section with contact assignment */}
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center w-full max-w-full">
                        <div className="font-medium text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">
                          {isFieldAlreadySigned 
                            ? '✓ Signed' 
                            : presignedFields.has(field.id)
                              ? 'Presigned'
                              : (isAssignedToCurrentUser ? (field.userName || user?.name || 'Your Name') : field.userName || 'User')
                          }
                        </div>
                        {!isFieldAlreadySigned && !presignedFields.has(field.id) && fieldHeight >= 50 && (
                          <div className="text-gray-600 text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">{field.userEmail || ''}</div>
                        )}
                        {isFieldAlreadySigned && (
                          <div className="text-green-700 truncate text-xs">
                            Ready to send
                          </div>
                        )}
                        {isFieldAlreadySigned && field.signedAt && (
                          <div className="text-green-600 truncate text-xs space-y-1">
                            <div>IP: {field.signedIP || 'N/A'}</div>
                            <div>Signed: {new Date(field.signedAt).toLocaleDateString()}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : fieldHeight >= 50 ? (
                  // Medium height field: Show icon + content centered with email but no "Signature" label
                  <div className="flex-1 flex items-center justify-center">
                    <div className="flex items-center space-x-2 w-full min-w-0">
                      <Pen className={`h-3 w-3 flex-shrink-0 ${getIconColor()}`} />
                      <div className="text-center w-full max-w-full min-w-0">
                        <div className="font-medium text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">
                          {isFieldAlreadySigned 
                            ? '✓ Signed' 
                            : presignedFields.has(field.id)
                              ? 'Presigned'
                              : (isAssignedToCurrentUser ? (field.userName || user?.name || 'Your Name') : field.userName || 'User')
                          }
                        </div>
                        {!isFieldAlreadySigned && !presignedFields.has(field.id) && (
                          <div className="text-gray-600 text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">{field.userEmail || ''}</div>
                        )}
                        {isFieldAlreadySigned && (
                          <div className="text-green-700 truncate text-xs">
                            Ready to send
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : fieldWidth >= 150 ? (
                  // Wide but short field: Show icon + label + content on same line
                  <div className="flex-1 flex items-center w-full min-w-0">
                    {/* Icon and label on the left */}
                    <div className="flex-shrink-0 flex items-center space-x-1 mr-3">
                      <Pen className={`h-3 w-3 ${getIconColor()}`} />
                      <span className={`font-medium text-xs ${getTextColor()}`}>
                        Signature
                      </span>
                    </div>
                    
                    {/* Content on the right */}
                    <div className="flex-1 text-center min-w-0">
                      <div className="font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                        {isFieldAlreadySigned 
                          ? '✓ Signed' 
                          : presignedFields.has(field.id)
                            ? 'Presigned'
                            : (isAssignedToCurrentUser ? (field.userName || user?.name || 'Your Name') : field.userName || 'User')
                        }
                      </div>
                    </div>
                  </div>
                ) : (
                  // Short field: Show icon and content on same line
                  <div className="flex-1 flex items-center w-full min-w-0">
                    {/* Icon on the left */}
                    <div className="flex-shrink-0 mr-2">
                      <Pen className={`h-3 w-3 ${getIconColor()}`} />
                    </div>
                    
                    {/* Content on the right - takes remaining space */}
                    <div className="flex-1 text-center min-w-0">
                      <div className="font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                        {isFieldAlreadySigned 
                          ? '✓ Signed' 
                          : presignedFields.has(field.id)
                            ? 'Presigned'
                            : (isAssignedToCurrentUser ? (field.userName || user?.name || 'Your Name') : field.userName || 'User')
                        }
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
      });
    }
    
    console.log(`=== PHASE 2 COMPLETE ===`);
    console.log(`Elements rendered total after Phase 2:`, renderedElements.length);
    
    // PHASE 3: Add any applied signatures (those the user just added but haven't saved)
    // CRITICAL FIX: Only show applied signatures for the current user
    appliedSignatures
      .filter(sig => (sig.page === targetPageNumber || !sig.page) && !sig.fieldId && sig.userId === user?.id)
      .forEach(signature => {
        const position = getAbsolutePosition(
          signature.position.x, 
          signature.position.y, 
          signature.position.width, 
          signature.position.height
        );
        
        renderedElements.push(
          <div
            key={`applied-free-${signature.id}`}
            className="absolute"
            style={{
              left: position.left,
              top: position.top,
              width: position.width,
              height: position.height,
              zIndex: 50
            }}
          >
            <img
              src={signature.data}
              alt="Applied Signature"
              className="max-w-full max-h-full"
              style={{ maxWidth: '100%', maxHeight: '100%' }}
              onError={(e) => {
                console.error('Error loading freshly applied signature:', e);
                e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                e.target.alt = 'Signature unavailable';
                e.target.style.background = '#f0f0f0';
              }}
            />
          </div>
        );
      });
    
    console.log(`DEBUG: Rendered ${renderedElements.length} total signature elements`);
    console.log(`DEBUG: Actual signatures rendered:`, unifiedSignatures.length);
    console.log(`DEBUG: Fields with actual signatures:`, Array.from(renderedFieldIds));
    
    // FINAL VALIDATION: Check for potential duplicates
    validateRenderedElements(renderedElements, unifiedSignatures, unifiedFields);
    
    return renderedElements;
  };
  
  // Validation function to detect potential rendering issues
  const validateRenderedElements = (elements, signatures, fields) => {
    console.log('=== SIGNATURE RENDERING VALIDATION ===');
    
    // Check for duplicate keys
    const keys = elements.map(el => el.key);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      console.error('❌ VALIDATION ERROR: Duplicate React keys detected!', {
        totalKeys: keys.length,
        uniqueKeys: uniqueKeys.size,
        duplicates: keys.filter((key, index) => keys.indexOf(key) !== index)
      });
    } else {
      console.log('✅ VALIDATION PASSED: No duplicate React keys');
    }
    
    // Check for field-signature consistency
    const signatureFieldIds = signatures.filter(s => s.fieldId).map(s => String(s.fieldId));
    const fieldsWithSignatures = fields.filter(f => f.signedAt).map(f => String(f.id));
    const allSignedFieldIds = [...new Set([...signatureFieldIds, ...fieldsWithSignatures])];
    
    console.log('✅ VALIDATION SUMMARY:', {
      totalElements: elements.length,
      signatureFieldIds: signatureFieldIds.length,
      fieldsWithSignedAt: fieldsWithSignatures.length,
      totalSignedFields: allSignedFieldIds.length,
      mappedFields: signatureState.fieldToSignatureMap.size
    });
    
    console.log('=== END VALIDATION ===');
  };

  // Render all form fields - EXACT same logic as signatures
  const renderAllFormFields = () => {
    console.log('renderAllFormFields called. formFields:', formFields);
    console.log('pageNumber:', pageNumber);
    console.log('existingFormFields:', existingFormFields);
    
    // In continuous mode, render form fields for all visible pages
    if (viewMode === 'continuous') {
      const renderedElements = [];
      const pagesToRender = Array.from(visiblePages).length > 0 ? Array.from(visiblePages) : [pageNumber];
      
      pagesToRender.forEach(currentPageNum => {
        const pageElement = pageRefs.current[currentPageNum]?.querySelector('.react-pdf__Page__canvas');
        if (!pageElement) return;
        
        const elements = renderFormFieldsForPage(currentPageNum, pageElement);
        renderedElements.push(...elements);
      });
      
      return renderedElements;
    }
    
    // Get the actual PDF page element for relative positioning (paged mode)
    const pageElement = documentRef.current?.querySelector('.react-pdf__Page');
    if (!pageElement) {
      console.log('PDF page element not found, skipping form field rendering');
      return [];
    }
    
    return renderFormFieldsForPage(pageNumber, pageElement);
  };
  
  // Helper function to render form fields for a specific page
  const renderFormFieldsForPage = (targetPageNumber, pageElement) => {
    const renderedElements = [];
    
    // Get page dimensions for coordinate conversion
    const pageRect = pageElement.getBoundingClientRect();
    
    // In continuous mode, positions are relative to the page container, not document
    let pageOffsetX = 0;
    let pageOffsetY = 0;
    
    if (viewMode !== 'continuous') {
      // In paged mode, calculate relative to document container
      const containerRect = documentRef.current?.getBoundingClientRect();
      pageOffsetX = pageRect.left - containerRect.left;
      pageOffsetY = pageRect.top - containerRect.top;
    }
    
    console.log('Form field page positioning data:', {
      pageRect: { width: pageRect.width, height: pageRect.height, left: pageRect.left, top: pageRect.top },
      pageOffset: { x: pageOffsetX, y: pageOffsetY },
      scale: scale,
      viewMode: viewMode
    });

    // Track which fields have actual form field data
    const renderedFieldIds = new Set();
    
    // PHASE 1: Render actual form fields from database (same as signatures priority)
    if (existingFormFields && existingFormFields.formFields) {
      const formFieldsForCurrentPage = existingFormFields.formFields.filter(formField => {
        const fieldPage = parseInt(formField.coordinates?.pageNumber || formField.coordinates?.page || 1);
        return fieldPage === targetPageNumber;
      });
      
      console.log(`=== PHASE 1: ACTUAL FORM FIELDS ===`);
      console.log(`Found ${formFieldsForCurrentPage.length} form fields for page ${targetPageNumber}`);
      
      formFieldsForCurrentPage.forEach((formField) => {
        console.log(`DEBUG: PHASE 1 - Rendering actual form field from database:`, formField.id);
        
        // Only render if field actually has a value (is filled)
        if (!formField.fieldValue || formField.fieldValue.trim() === '') {
          console.log(`DEBUG: PHASE 1 - Skipping form field ${formField.id} - no value`);
          return;
        }
        
        // Parse coordinates from form field (handle both formats like signatures do)
        let coordinates = null;
        if (formField.coordinates) {
          try {
            if (typeof formField.coordinates === 'string') {
              coordinates = JSON.parse(formField.coordinates);
            } else {
              coordinates = formField.coordinates;
            }
          } catch (e) {
            console.error(`Failed to parse coordinates for form field ${formField.id}:`, e);
            return;
          }
        }

        if (!coordinates || ((!coordinates.pdfX && coordinates.pdfX !== 0) && (!coordinates.x && coordinates.x !== 0))) {
          console.warn(`Form field ${formField.id} has no valid coordinates, skipping`);
          return;
        }

        // Track that this field has actual data
        if (formField.fieldId) {
          renderedFieldIds.add(formField.fieldId);
        }

        const fieldConfig = FORM_FIELD_TYPES[formField.fieldType] || FORM_FIELD_TYPES.text;
        const FieldIcon = fieldConfig.icon;

        // Use coordinates from parsed data (same as signature logic)
        const x = coordinates?.x || 100;
        const y = coordinates?.y || 100;  
        const width = coordinates?.width || 150;
        const height = coordinates?.height || 30;

        const position = getAbsolutePosition(x, y, width, height);

        renderedElements.push(
          <div
            key={`filled-${formField.id}`}
            className="absolute border-2 border-green-500 bg-white bg-opacity-90 rounded shadow-sm overflow-hidden"
            style={{
              left: position.left,
              top: position.top,
              width: position.width,
              height: position.height,
              minWidth: '150px',
              minHeight: '30px',
              zIndex: 20,
              pointerEvents: 'auto'
            }}
          >
            <div className="w-full h-full flex items-center px-2 py-1">
              <div className="flex items-center space-x-1 flex-1">
                <span className="text-xs truncate text-green-700">
                  {formField.fieldValue}
                </span>
              </div>
              <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
            </div>
          </div>
        );
      });
    }

    // PHASE 2: Render form field placeholders from requests AND unfilled form fields
    const fieldsForCurrentPage = formFields.filter(field => {
      const fieldPage = parseInt(field.coordinates?.pageNumber || field.coordinates?.page || 1);
      return fieldPage === targetPageNumber;
    });
    
    // Also include unfilled FormField records as placeholders
    let unfilledFormFields = [];
    if (existingFormFields && existingFormFields.formFields) {
      unfilledFormFields = existingFormFields.formFields.filter(formField => {
        // Include if no value and not already rendered in PHASE 1
        const hasValue = formField.fieldValue && formField.fieldValue.trim() !== '';
        const fieldPage = parseInt(formField.coordinates?.pageNumber || formField.coordinates?.page || 1);
        const isCurrentPage = fieldPage === targetPageNumber;
        const notAlreadyRendered = !renderedFieldIds.has(formField.fieldId || formField.id);
        
        return !hasValue && isCurrentPage && notAlreadyRendered;
      }).map(formField => ({
        // Transform FormField to look like a form field from requests
        id: formField.fieldId || formField.id,
        fieldId: formField.fieldId || formField.id,
        type: formField.fieldType,
        fieldType: formField.fieldType,
        coordinates: formField.coordinates,
        userId: formField.userId,
        userName: formField.User?.name,
        userEmail: formField.userEmail // Use the assigned user's email, not the creator's
      }));
    }
    
    const allFieldsForCurrentPage = [...fieldsForCurrentPage, ...unfilledFormFields];

    console.log(`=== PHASE 2: FORM FIELD PLACEHOLDERS ===`);
    console.log(`Found ${fieldsForCurrentPage.length} form field placeholders from requests for page ${targetPageNumber}`);
    console.log(`Found ${unfilledFormFields.length} unfilled form fields for page ${targetPageNumber}`);
    console.log(`Total placeholders: ${allFieldsForCurrentPage.length}`);

    allFieldsForCurrentPage.forEach((field) => {
      const fieldId = field.fieldId || field.id;
      
      // Skip if this field already has actual data rendered
      if (renderedFieldIds.has(fieldId)) {
        console.log(`DEBUG: PHASE 2 - Field ${fieldId} already has data, skipping placeholder`);
        return;
      }
      
      console.log(`DEBUG: PHASE 2 - Rendering form field placeholder:`, fieldId);
      
      // Parse coordinates from form field (handle both formats like signatures do)
      let coordinates = null;
      if (field.coordinates) {
        try {
          if (typeof field.coordinates === 'string') {
            coordinates = JSON.parse(field.coordinates);
          } else {
            coordinates = field.coordinates;
          }
        } catch (e) {
          console.error(`Failed to parse coordinates for form field ${fieldId}:`, e);
          return;
        }
      }

      if (!coordinates || ((!coordinates.pdfX && coordinates.pdfX !== 0) && (!coordinates.x && coordinates.x !== 0))) {
        console.warn(`Form field ${fieldId} has no valid coordinates, skipping`);
        return;
      }

      // Check if current user is authorized to edit this form field
      // Check if field is assigned to current user (via userEmail from assignedTo)
      const isCurrentUserField = field.userEmail?.toLowerCase() === user?.email?.toLowerCase();
      
      // Check if accessing via form field token and field is for this user
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      let isFormFieldTokenUser = false;
      
      if (token) {
        // Check if token user email matches field user email
        const backendSignerEmail = document?.signatureAuth?.signerEmail;
        if (backendSignerEmail) {
          isFormFieldTokenUser = field.userEmail?.toLowerCase() === backendSignerEmail.toLowerCase();
          console.log('DEBUG: Form field token matching via backend signerEmail:', {
            fieldUserEmail: field.userEmail,
            backendSignerEmail,
            isMatch: isFormFieldTokenUser
          });
        } else if (token.includes('.') && token.split('.').length >= 2) {
          try {
            // Fallback for legacy JWT tokens – decode payload
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            isFormFieldTokenUser = field.userEmail?.toLowerCase() === tokenPayload.signer_email?.toLowerCase();
            console.log('DEBUG: Form field token matching via decoded JWT:', {
              fieldUserEmail: field.userEmail,
              tokenSignerEmail: tokenPayload.signer_email,
              isMatch: isFormFieldTokenUser
            });
          } catch (e) {
            console.warn('Could not decode token for form field matching:', e);
          }
        }
      }
      
      const isAssignedToCurrentUser = isCurrentUserField || isFormFieldTokenUser;
      
      console.log('DEBUG: Form field authorization check:', {
        fieldId,
        userEmail: field.userEmail,
        currentUserEmail: user?.email,
        isCurrentUserField,
        isFormFieldTokenUser,
        hasFormFieldRequest,
        isAssignedToCurrentUser
      });

      const fieldConfig = FORM_FIELD_TYPES[field.fieldType || field.type] || FORM_FIELD_TYPES.text;
      const FieldIcon = fieldConfig.icon;
      const fieldValue = formFieldValues[fieldId] || '';
      const isEditing = editingFormField === fieldId;
      // Form fields should only show as "completed" (green) after document is finalized
      // Before finalization, they show as "filled" (blue) when they have values
      const isFilled = !!fieldValue || completedFormFields.has(fieldId);
      const isCompleted = isFilled && isDocumentFinalized;

      // Use coordinates from parsed data (same as signature logic)
      const x = coordinates?.x || 100;
      const y = coordinates?.y || 100;  
      const width = coordinates?.width || 150;
      const height = coordinates?.height || 30;

      const position = getAbsolutePosition(x, y, width, height);

      // Determine visual styling based on authorization and completion
      let fieldClasses, fieldStyle, clickHandler;
      
      if (!isAssignedToCurrentUser) {
        // Field not assigned to current user - show as read-only
        fieldClasses = `absolute border-2 rounded transition-all border-gray-400 bg-gray-100 cursor-not-allowed`;
        fieldStyle = {
          left: position.left,
          top: position.top,
          width: position.width,
          height: position.height,
          minWidth: '150px',
          minHeight: '30px',
          zIndex: 20,
          pointerEvents: 'auto',
          opacity: 0.6
        };
        clickHandler = () => {
          alert(`This form field is assigned to ${field.userEmail || 'another user'}. You cannot edit it.`);
        };
      } else {
        // Field assigned to current user - show as editable
        fieldClasses = `absolute border-2 rounded cursor-pointer transition-all ${
          isCompleted 
            ? 'border-green-500 bg-green-50' 
            : isFilled
              ? 'border-blue-500 bg-blue-50'
              : 'border-blue-500 bg-blue-50'
        } ${isEditing ? 'shadow-lg ring-2 ring-blue-300' : 'hover:shadow-md'}`;
        fieldStyle = {
          left: position.left,
          top: position.top,
          width: position.width,
          height: position.height,
          minWidth: '150px',
          minHeight: '30px',
          zIndex: 20,
          pointerEvents: 'auto'
        };
        clickHandler = () => handleFormFieldClick(field);
      }

      renderedElements.push(
        <div
          key={fieldId}
          className={fieldClasses}
          style={fieldStyle}
          onClick={clickHandler}
        >
          {!isAssignedToCurrentUser ? (
            // Show read-only content for unauthorized fields
            <div className="w-full h-full flex items-center px-2 py-1">
              <div className="flex items-center space-x-1 flex-1">
                <span className="text-xs truncate text-gray-600">
                  {fieldValue || `Assigned to ${field.userEmail || 'another user'}`}
                </span>
              </div>
              {isCompleted && (
                <CheckCircle className="h-3 w-3 text-gray-600 flex-shrink-0" />
              )}
            </div>
          ) : isEditing ? (
            <input
              type={(field.fieldType || field.type) === 'email' ? 'email' : (field.fieldType || field.type) === 'phone' ? 'tel' : 'text'}
              value={formFieldValues[fieldId] || ''}
              onChange={(e) => handleFormFieldChange(fieldId, e.target.value)}
              onBlur={() => handleFormFieldSubmit(field)}
              onKeyDown={(e) => e.key === 'Enter' && handleFormFieldSubmit(field)}
              placeholder={fieldConfig.placeholder}
              className="w-full h-full px-2 py-1 text-sm border-none outline-none bg-transparent"
              autoFocus
            />
          ) : (
            <div className="w-full h-full flex items-center px-2 py-1">
              <div className="flex items-center space-x-1 flex-1">
                <span className={`text-xs truncate ${isCompleted ? 'text-green-700' : 'text-blue-700'}`}>
                  {fieldValue || fieldConfig.placeholder}
                </span>
              </div>
              {isCompleted && (
                <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
              )}
            </div>
          )}
        </div>
      );
    });

    console.log(`DEBUG: Rendered ${renderedElements.length} total form field elements`);
    console.log(`DEBUG: Actual form fields rendered:`, existingFormFields?.formFields?.length || 0);
    console.log(`DEBUG: Fields with actual data:`, Array.from(renderedFieldIds));
    
    return renderedElements;
  };

  // Form field interaction handlers
  const handleFormFieldClick = (field) => {
    const fieldId = field.fieldId || field.id;
    console.log('Form field clicked:', field);
    
    // Check if current user is authorized to edit this form field
    // Check if field is assigned to current user (via userEmail from assignedTo)
    const isCurrentUserField = field.userEmail?.toLowerCase() === user?.email?.toLowerCase();
    
    // Check if accessing via form field token and field is for this user
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    let isFormFieldTokenUser = false;
    
    if (token) {
      // Check if token user email matches field user email
      const backendSignerEmail = document?.signatureAuth?.signerEmail;
      if (backendSignerEmail) {
        isFormFieldTokenUser = field.userEmail?.toLowerCase() === backendSignerEmail.toLowerCase();
      } else if (token.includes('.') && token.split('.').length >= 2) {
        try {
          // Fallback for legacy JWT tokens – decode payload
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          isFormFieldTokenUser = field.userEmail?.toLowerCase() === tokenPayload.signer_email?.toLowerCase();
        } catch (e) {
          console.warn('Could not decode token for form field matching:', e);
        }
      }
    }
    
    const isAssignedToCurrentUser = isCurrentUserField || isFormFieldTokenUser;
    
    if (!isAssignedToCurrentUser) {
      alert(`This form field is assigned to ${field.userEmail || 'another user'}. You cannot edit it.`);
      return;
    }
    
    setEditingFormField(fieldId);
  };

  // Download functionality
  const handleDownload = async () => {
    try {
      console.log('Starting download for document:', documentId);
      
      // Get the signature token from URL if present
      const params = new URLSearchParams(window.location.search);
      const signatureToken = params.get('token');
      
      // Build download URL
      let downloadUrl = `${API_URL}/documents/${documentId}/download`;
      if (signatureToken) {
        downloadUrl += `?token=${encodeURIComponent(signatureToken)}`;
      }
      
      // Set up headers
      const headers = {};
      if (!signatureToken && user?.token) {
        headers['Authorization'] = `Bearer ${user.token}`;
      }
      
      // Fetch the document
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'document.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else if (document?.originalFileName) {
        filename = document.originalFileName.toLowerCase().endsWith('.pdf')
          ? document.originalFileName
          : `${document.originalFileName}.pdf`;
      } else if (document?.name) {
        filename = document.name.includes('.pdf') ? document.name : `${document.name}.pdf`;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      window.document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
      
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };



  const handleFormFieldChange = (fieldId, value) => {
    setFormFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleFormFieldSubmit = async (field) => {
    try {
      const fieldId = field.fieldId || field.id;
      const value = formFieldValues[fieldId] || '';
      console.log('Submitting form field:', fieldId, 'with value:', value);
      
      // Submit to backend - fix: pass value directly, not as object
      await formFieldService.submitFormFieldData(fieldId, value, null);
      
      console.log('Form field submitted successfully');
      setEditingFormField(null);
      
      // Update the form field in our local state to reflect completion
      setFormFields(prev => 
        prev.map(f => 
          (f.fieldId || f.id) === fieldId 
            ? { ...f, fieldValue: value, completedAt: new Date().toISOString() }
            : f
        )
      );

      // Add to completed form fields set for visual confirmation (like signatures)
      setCompletedFormFields(prev => new Set([...prev, fieldId]));
      
      // Refresh form fields data to get updated state
      if (refreshTrigger) {
        setTimeout(() => {
          window.location.reload(); // Simple refresh to update form field data
        }, 500);
      }
      
    } catch (error) {
      console.error('Error submitting form field:', error);
      
      // Handle specific access denied error
      if (error.error === 'Access denied. This form field was not assigned to your email address.') {
        alert(`Access denied: This form field was assigned to ${error.assignedTo}, but you are accessing it as ${error.yourEmail}. Please use the correct email link to fill this field.`);
      } else if (error.error === 'Authentication required to fill form fields') {
        alert('You must be logged in or use the correct form field link to fill this field.');
      } else {
        alert(error.error || error.message || 'Failed to save form field data. Please try again.');
      }
    }
  };

  // Function to render annotations for the current page
  // State for annotation modal
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);

  // Show annotation details modal
  const showAnnotationDetails = (annotation) => {
    setSelectedAnnotation(annotation);
    setShowAnnotationModal(true);
  };

  // Text highlighting component
  const TextHighlight = ({ annotation, searchText, pageNumber, scale, onClick }) => {
    const [textPositions, setTextPositions] = useState([]);
    const [searchFailed, setSearchFailed] = useState(false);
    
    useEffect(() => {
      const findTextInPage = async () => {
        if (!fileUrl || !searchText) {
          setSearchFailed(true);
          return;
        }
        
        try {
          console.log(`🔍 Searching for text: "${searchText}" on page ${pageNumber}`);
          
          // Wait for page to render and try multiple times to find the page element
          let pageElement = null;
          let attempts = 0;
          const maxAttempts = 10;
          
          while (!pageElement && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200));
            pageElement = window.document.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`);
            attempts++;
            
            if (!pageElement) {
              console.log(`🔄 Attempt ${attempts}: Page element not found, trying again...`);
            }
          }
          
          if (!pageElement) {
            console.error(`❌ Could not find rendered page element for page ${pageNumber} after ${maxAttempts} attempts`);
            // Try alternative selectors
            pageElement = window.document.querySelector(`.react-pdf__Page`);
            if (!pageElement) {
              console.error(`❌ Could not find any PDF page element`);
              setSearchFailed(true);
              return;
            }
            console.log(`✅ Found alternative page element`);
          }
          
          const pageRect = pageElement.getBoundingClientRect();
          const containerRect = documentRef.current?.getBoundingClientRect();
          
          // Load PDF document using PDF.js
          const loadingTask = pdfjs.getDocument(fileUrl);
          const pdfDoc = await loadingTask.promise;
          const page = await pdfDoc.getPage(pageNumber);
          const textContent = await page.getTextContent();
          const viewport = page.getViewport({ scale: 1 });
          
          console.log(`📄 Page ${pageNumber} PDF viewport:`, { width: viewport.width, height: viewport.height });
          console.log(`📄 Page ${pageNumber} rendered size:`, { width: pageRect.width, height: pageRect.height });
          console.log(`📄 Page ${pageNumber} text items:`, textContent.items.length);
          
          // Calculate scale factors to convert from PDF coordinate space to rendered page space
          const scaleX = pageRect.width / viewport.width;
          const scaleY = pageRect.height / viewport.height;
          
          console.log(`📐 Coordinate scale factors - X: ${scaleX}, Y: ${scaleY}`);
          
          // Build complete text string with position mapping
          let fullText = '';
          let charPositions = [];
          
          textContent.items.forEach((item, itemIndex) => {
            if (item.str && item.str.trim()) {
              const itemText = item.str;
              const startIndex = fullText.length;
              fullText += itemText;
              
              // Calculate character positions for this text item
              // Convert from PDF coordinate space to rendered page coordinate space
              const charWidth = (item.width / itemText.length) * scaleX;
              const baseX = item.transform[4] * scaleX;
              const baseY = (viewport.height - item.transform[5] - item.height) * scaleY;
              
              for (let i = 0; i < itemText.length; i++) {
                charPositions.push({
                  char: itemText[i],
                  x: baseX + (i * charWidth),
                  y: baseY,
                  width: charWidth,
                  height: (item.height || 12) * scaleY,
                  itemIndex,
                  charIndex: i,
                  textIndex: startIndex + i
                });
              }
            }
          });
          
          console.log(`📝 Built text of length ${fullText.length}, char positions: ${charPositions.length}`);
          console.log(`📝 First 200 chars: "${fullText.substring(0, 200)}"`);
          
          // Normalize search text
          const normalizeText = (text) => text.toLowerCase().replace(/\s+/g, ' ').trim();
          const normalizedFullText = normalizeText(fullText);
          const normalizedSearchText = normalizeText(searchText);
          
          console.log(`🔍 Normalized search: "${normalizedSearchText}"`);
          
          // Find all occurrences
          const highlights = [];
          let searchIndex = 0;
          
          while ((searchIndex = normalizedFullText.indexOf(normalizedSearchText, searchIndex)) !== -1) {
            const endIndex = searchIndex + normalizedSearchText.length;
            console.log(`✅ Found match at positions ${searchIndex}-${endIndex}`);
            
            // Get character positions for this match
            const matchCharPositions = charPositions.slice(searchIndex, endIndex);
            
            if (matchCharPositions.length > 0) {
              // Group consecutive characters into highlight spans
              const spans = [];
              let currentSpan = null;
              
              matchCharPositions.forEach((charPos, index) => {
                if (!currentSpan || 
                    Math.abs(charPos.y - currentSpan.y) > 5 || // New line
                    charPos.x - (currentSpan.x + currentSpan.width) > charPos.width * 2) { // Large gap
                  
                  // Start new span
                  if (currentSpan) spans.push(currentSpan);
                  currentSpan = {
                    x: charPos.x,
                    y: charPos.y,
                    width: charPos.width,
                    height: charPos.height,
                    text: charPos.char
                  };
                } else {
                  // Extend current span
                  currentSpan.width = (charPos.x + charPos.width) - currentSpan.x;
                  currentSpan.text += charPos.char;
                }
              });
              
              if (currentSpan) spans.push(currentSpan);
              
              highlights.push(...spans);
              console.log(`📍 Created ${spans.length} highlight spans for this match`);
            }
            
            searchIndex++;
          }
          
          // Enhanced fallback search with multiple strategies
          if (highlights.length === 0) {
            console.log(`🔄 No exact match found, trying fallback strategies...`);
            
            // Strategy 1: Try partial matches (removing some words)
            if (normalizedSearchText.length > 20) {
              const words = normalizedSearchText.split(' ');
              for (let wordCount = Math.max(3, words.length - 2); wordCount >= 3; wordCount--) {
                const partialText = words.slice(0, wordCount).join(' ');
                searchIndex = 0;
                
                while ((searchIndex = normalizedFullText.indexOf(partialText, searchIndex)) !== -1) {
                  const endIndex = searchIndex + partialText.length;
                  const matchCharPositions = charPositions.slice(searchIndex, endIndex);
                  
                  if (matchCharPositions.length > 0) {
                    const spans = [];
                    let currentSpan = null;
                    
                    matchCharPositions.forEach((charPos, index) => {
                      if (!currentSpan || 
                          Math.abs(charPos.y - currentSpan.y) > 5 || 
                          charPos.x - (currentSpan.x + currentSpan.width) > charPos.width * 2) {
                        
                        if (currentSpan) spans.push(currentSpan);
                        currentSpan = {
                          x: charPos.x,
                          y: charPos.y,
                          width: charPos.width,
                          height: charPos.height,
                          text: charPos.char,
                          isPartial: true
                        };
                      } else {
                        currentSpan.width = (charPos.x + charPos.width) - currentSpan.x;
                        currentSpan.text += charPos.char;
                      }
                    });
                    
                    if (currentSpan) spans.push(currentSpan);
                    highlights.push(...spans);
                    console.log(`📍 Created partial match highlight for "${partialText}"`);
                    break;
                  }
                  searchIndex++;
                }
                
                if (highlights.length > 0) break;
              }
            }
            
            // Strategy 2: Try key legal terms if still no match
            if (highlights.length === 0) {
              const legalKeywords = ['consultant', 'services', 'agreement', 'client', 'shall', 'performance', 'standards', 'exhibit', 'statement', 'work'];
              const availableKeywords = legalKeywords.filter(keyword => 
                normalizedSearchText.includes(keyword) && normalizedFullText.includes(keyword)
              );
              
              for (const keyword of availableKeywords.slice(0, 1)) { // Try first available keyword
                searchIndex = 0;
                while ((searchIndex = normalizedFullText.indexOf(keyword, searchIndex)) !== -1) {
                  const endIndex = searchIndex + keyword.length;
                  const matchCharPositions = charPositions.slice(searchIndex, endIndex);
                  
                  if (matchCharPositions.length > 0) {
                    const firstChar = matchCharPositions[0];
                    const lastChar = matchCharPositions[matchCharPositions.length - 1];
                    
                    highlights.push({
                      x: firstChar.x,
                      y: firstChar.y,
                      width: (lastChar.x + lastChar.width) - firstChar.x,
                      height: firstChar.height,
                      text: keyword,
                      isPartial: true
                    });
                    
                    console.log(`📍 Created legal term highlight for "${keyword}"`);
                    break;
                  }
                  searchIndex++;
                }
                
                if (highlights.length > 0) break;
              }
            }
          }
          
          // Validate and clean up highlights
          const validHighlights = highlights.filter(highlight => {
            const isValid = highlight && 
                           typeof highlight.x === 'number' && !isNaN(highlight.x) &&
                           typeof highlight.y === 'number' && !isNaN(highlight.y) &&
                           typeof highlight.width === 'number' && !isNaN(highlight.width) &&
                           typeof highlight.height === 'number' && !isNaN(highlight.height) &&
                           highlight.width > 0 && highlight.height > 0;
                           
            if (!isValid) {
              console.warn(`⚠️ Invalid highlight filtered out:`, highlight);
            }
            return isValid;
          });
          
          console.log(`✨ Final highlights: ${validHighlights.length} valid out of ${highlights.length} total`);
          if (validHighlights.length > 0) {
            console.log(`✨ Sample highlight position:`, validHighlights[0]);
            console.log(`✨ Page element size:`, { width: pageRect.width, height: pageRect.height });
            console.log(`✨ PDF viewport size:`, { width: viewport.width, height: viewport.height });
          }
          
          setTextPositions(validHighlights);
          setSearchFailed(validHighlights.length === 0);
          
        } catch (error) {
          console.error('❌ Error finding text positions:', error);
          console.error('❌ Error details:', {
            searchText: searchText,
            pageNumber: pageNumber,
            fileUrl: !!fileUrl,
            error: error.message
          });
          setSearchFailed(true);
        }
      };
      
      findTextInPage();
    }, [fileUrl, searchText, pageNumber]);
    
    const riskLevel = annotation.content?.riskLevel || 'LOW';
    const getRiskColor = () => {
      switch (riskLevel) {
        case 'HIGH': return '#ef4444';
        case 'MEDIUM': return '#f59e0b';
        case 'LOW': return '#10b981';
        default: return '#6b7280';
      }
    };
    
    // If text search failed, render a positioned annotation as fallback
    if (searchFailed || textPositions.length === 0) {
      // Position fallback annotation in a more predictable location
      const fallbackX = 100;
      const fallbackY = 150 + (Math.abs(annotation.id?.charCodeAt(0) || 0) % 5) * 60; // Spread vertically based on ID
      
      return (
        <div
          className="absolute cursor-pointer hover:shadow-lg transition-shadow border-2 rounded-lg bg-white bg-opacity-95"
          style={{
            left: `${fallbackX}px`,
            top: `${fallbackY}px`,
            width: `220px`,
            minHeight: `45px`,
            borderColor: getRiskColor(),
            zIndex: 30,
            pointerEvents: 'auto'
          }}
          onClick={onClick}
          title={`${annotation.content?.text || 'AI Analysis'} - Click for details (Text not found: "${searchText?.substring(0, 30)}...")`}
        >
          <div className="p-2 h-full flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <span 
                className="text-xs font-semibold px-2 py-1 rounded-full text-white"
                style={{ 
                  backgroundColor: getRiskColor(),
                  fontSize: '10px'
                }}
              >
                AI {riskLevel}
              </span>
              <div 
                className="w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: getRiskColor() }}
              >
                {riskLevel === 'HIGH' ? '!' : riskLevel === 'MEDIUM' ? '?' : 'i'}
              </div>
            </div>
            <div 
              className="flex-1 text-gray-700 text-xs overflow-hidden"
              style={{ fontSize: '9px' }}
            >
              {annotation.content?.text && annotation.content.text.length > 35 
                ? `${annotation.content.text.substring(0, 35)}...` 
                : annotation.content?.text || 'AI Analysis'
              }
            </div>
            <div className="text-xs text-gray-500 mt-1" style={{ fontSize: '8px' }}>
              (Text highlight failed)
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <>
        {/* Text highlights - proper highlighting style */}
        {textPositions.map((position, index) => (
          <div
            key={`${annotation.id}-highlight-${index}`}
            className="absolute cursor-pointer transition-opacity"
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: `${position.width}px`,
              height: `${position.height}px`,
              backgroundColor: getRiskColor(),
              opacity: position.isPartial ? 0.4 : 0.35,
              zIndex: 25,
              pointerEvents: 'auto',
              borderRadius: '2px',
              // Add subtle border for better visibility
              border: `1px solid ${getRiskColor()}40`
            }}
            onClick={onClick}
            onMouseEnter={(e) => {
              e.target.style.opacity = position.isPartial ? 0.6 : 0.5;
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = position.isPartial ? 0.4 : 0.35;
            }}
            title={`${annotation.content?.text || 'AI Analysis'} - "${position.text}" - Click for details`}
          />
        ))}
        
        {/* Risk indicator badge - positioned at the end of the first highlight */}
        {textPositions.length > 0 && (
          <div
            className="absolute cursor-pointer hover:scale-110 transition-transform flex items-center justify-center"
            style={{
              left: `${textPositions[0].x + textPositions[0].width + 3}px`,
              top: `${textPositions[0].y - 2}px`,
              zIndex: 35,
              pointerEvents: 'auto'
            }}
            onClick={onClick}
          >
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md border border-white"
              style={{ 
                backgroundColor: getRiskColor(),
                fontSize: `${9}px`
              }}
            >
              {riskLevel === 'HIGH' ? '!' : riskLevel === 'MEDIUM' ? '?' : 'i'}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderAnnotations = () => {
    if (!showAnnotations || !annotations || annotations.length === 0) {
      return [];
    }
    
    // In continuous mode, annotations are rendered per page
    if (viewMode === 'continuous') {
      // Annotations are rendered within each page container
      return [];
    }

    const renderedAnnotations = [];
    
    // Horizontal offset helper (same as signature rendering)
    const pageOffsetX = (() => {
      if (!documentRef.current) return 0;
      const pageEl = documentRef.current.querySelector('.react-pdf__Page');
      if (!pageEl) return 0;
      const containerRect = documentRef.current.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();
      return pageRect.left - containerRect.left;
    })();

    const leftPx = (pdfX) => `${pageOffsetX + pdfX * scale}px`;
    
    // Filter annotations for current page
    const pageAnnotations = annotations.filter(annotation => 
      !annotation.page || annotation.page === pageNumber
    );
    
    // Rest of the rendering logic is the same as renderAnnotationsForPage
    pageAnnotations.forEach((annotation, index) => {
      const { content, type, page, id, aiGenerated } = annotation;
      
      // Render logic continues as before...
      // (This is a simplified placeholder - actual logic remains unchanged)
    });
    
    return renderedAnnotations;
  };
  
  // Helper function to render annotations for a specific page
  const renderAnnotationsForPage = (targetPageNumber) => {
    if (!showAnnotations || !annotations || annotations.length === 0) {
      return [];
    }
    
    const renderedAnnotations = [];
    
    // In continuous mode, no offset needed as positions are relative to page container
    const pageOffsetX = 0;
    const leftPx = (pdfX) => `${pageOffsetX + pdfX * scale}px`;
    
    // Filter annotations for target page
    const pageAnnotations = annotations.filter(annotation => 
      !annotation.page || annotation.page === targetPageNumber
    );

    pageAnnotations.forEach((annotation, index) => {
      const { content, type, page, id, aiGenerated } = annotation;
      
      // If annotation has searchText, render as text highlight
      if (content?.searchText && content?.requiresTextSearch) {
        renderedAnnotations.push(
          <TextHighlight
            key={`text-highlight-${id || index}`}
            annotation={annotation}
            searchText={content.searchText}
            pageNumber={targetPageNumber}
            scale={scale}
            onClick={() => showAnnotationDetails(annotation)}
          />
        );
        return;
      }
      
      // Fallback to positioned annotation for non-searchable content
      let x = content?.position || (100 + (index * 50));
      let y = content?.position || (100 + (index * 30));
      let width = 200;
      let height = 30;
      
      // Adjust size based on annotation type
      if (type === 'comment') {
        width = 300;
        height = 40;
      } else if (type === 'highlight') {
        width = 150;
        height = 20;
      } else if (type === 'redline') {
        width = 250;
        height = 25;
      }

      // Ensure annotation stays within page bounds
      const maxX = 800 - width;
      const maxY = 1100 - height;
      x = Math.min(x, maxX);
      y = Math.min(y, maxY);

      // Color based on annotation type and whether it's AI-generated
      const getAnnotationColor = () => {
        if (aiGenerated) {
          const riskLevel = content?.riskLevel || 'LOW';
          switch (riskLevel) {
            case 'HIGH': return '#ef4444';
            case 'MEDIUM': return '#f59e0b';
            case 'LOW': return '#10b981';
            default: return '#6b7280';
          }
        } else {
          return content?.color || '#3B82F6';
        }
      };

      const backgroundColor = getAnnotationColor();
      const textColor = aiGenerated ? '#FFFFFF' : '#1F2937';

      renderedAnnotations.push(
        <div
          key={`annotation-${id || index}`}
          className={`absolute rounded-lg shadow-sm border-2 z-30 cursor-pointer hover:shadow-lg transition-shadow ${
            aiGenerated ? 'border-red-300' : 'border-blue-300'
          }`}
          style={{
            left: leftPx(x),
            top: `${y * scale}px`,
            width: `${width * scale}px`,
            height: `${height * scale}px`,
            backgroundColor: backgroundColor + '20',
            borderColor: backgroundColor,
            transformOrigin: 'top left'
          }}
          onClick={() => showAnnotationDetails(annotation)}
        >
          <div className="p-2 h-full flex flex-col">
            {/* Annotation header */}
            <div className="flex items-center justify-between mb-1">
              <span 
                className="text-xs font-semibold px-2 py-1 rounded-full"
                style={{ 
                  backgroundColor: backgroundColor,
                  color: textColor,
                  fontSize: `${12 * scale}px`
                }}
              >
                {aiGenerated ? 'AI' : 'Manual'} {type.charAt(0).toUpperCase() + type.slice(1)}
              </span>
              {aiGenerated && (
                <span className="text-xs text-red-600 font-medium" style={{ fontSize: `${10 * scale}px` }}>
                  ⚠️
                </span>
              )}
            </div>
            
            {/* Annotation content */}
            <div 
              className="flex-1 text-gray-700 text-sm overflow-hidden"
              style={{ fontSize: `${11 * scale}px` }}
              title={content?.text || 'No content'}
            >
              {content?.text && content.text.length > 50 
                ? `${content.text.substring(0, 50)}...` 
                : content?.text || 'No content'
              }
            </div>
          </div>
        </div>
      );
    });

    return renderedAnnotations;
  };
  
  // Loading and error states
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error</div>
          <p className="text-gray-700 mb-6">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-white">
      
      {/* Signature notification banner - only shown to users with pending signature requests */}
      {hasSignatureRequest && signatureFields && signatureFields.length > 0 && !signatureFields.every(field => isFieldSigned(field.id)) && (
        <div className="bg-blue-50 border-b border-blue-200 p-3">
          <div className="flex items-center justify-center text-blue-700">
            <FileSignature size={20} className="mr-2" />
            <span className="font-medium">Your signature is required on this document.</span>
            <span className="mx-2">Please click on the highlighted signature fields or use the "Next Field" button above.</span>
          </div>
        </div>
      )}
      
      {/* Document content */}
      <div className="flex-1 relative overflow-auto bg-gray-50">
        {showSignatures ? (
          <div className="p-4">
            <SignatureHistory 
              documentId={documentId}
              isOwner={isOwner}
              onRefresh={async () => {
                console.log('DEBUG: SignatureHistory onRefresh triggered...');
                try {
                  setSignatureState(prev => ({ ...prev, isLoading: true }));
                  
                  const signatureData = await signatureService.getDocumentSignatures(documentId, signatureToken);
                  console.log('DEBUG: Refreshed signature data:', signatureData);
                  
                  let signatures = [];
                  let allSignatureFields = [];
                  
                  if (signatureData?.signatures) {
                    signatures = signatureData.signatures;
                    setExistingSignatures({ signatures });
                    console.log('DEBUG: Refreshed signatures:', signatures.length);
                  }
                  
                  if (signatureData?.requests?.length > 0) {
                    allSignatureFields = signatureData.requests.flatMap(req => {
                      if (req.signatureFields) {
                        return req.signatureFields.map(field => ({
                          ...field,
                          userId: req.userId,
                          userName: req.userName,
                          userEmail: req.userEmail
                        }));
                      }
                      return [];
                    });
                    setSignatureFields(allSignatureFields);
                    console.log('DEBUG: Refreshed signature fields:', allSignatureFields.length);
                  }
                  
                  // Clear applied signatures and update unified state
                  setAppliedSignatures([]);
                  updateSignatureState(signatures, allSignatureFields, []);
                  console.log('DEBUG: SignatureHistory refresh completed with unified state update');
                  
                } catch (error) {
                  console.error('Error refreshing signatures:', error);
                  setSignatureState(prev => ({ ...prev, isLoading: false }));
                }
              }}
              onClose={onSignatureViewClose}
            />
          </div>
        ) : showSignatureRequests ? (
          <div className="p-4">
            <SignatureRequestsPanel 
              documentId={documentId}
              onRequestSelect={(request) => {
                // Prefer fields from local state to guarantee shape matches rendering logic
                const fieldsForRequest = (signatureFields || []).filter(f => f.userId === request.userId);
                // Fallback to request payload if local state not populated yet
                const sourceFields = fieldsForRequest.length > 0 ? fieldsForRequest : (request.signatureFields || []);

                // Select the next unsigned field for this request, or fallback to the first field
                const nextField = sourceFields.find(f => !isFieldSigned(f.id)) || sourceFields[0];

                if (nextField) {
                  // Convert PDF-space coordinates to pixel-space for SignaturePlacement
                  let convertedField = nextField;
                  try {
                    const pageElement = documentRef.current?.querySelector('.react-pdf__Page');
                    const pageRect = pageElement?.getBoundingClientRect();
                    if (pdfPageDimensions && pageRect) {
                      const scaleX = pageRect.width / pdfPageDimensions.width;
                      const scaleY = pageRect.height / pdfPageDimensions.height;
                      convertedField = {
                        ...nextField,
                        x: (nextField.x || 0) * scaleX,
                        y: (nextField.y || 0) * scaleY,
                        width: (nextField.width || 200) * scaleX,
                        height: (nextField.height || 70) * scaleY,
                      };
                    }
                  } catch (e) {
                    // Fallback to raw field if conversion fails
                    console.warn('Field coordinate conversion failed, using raw field:', e);
                  }

                  setPageNumber(nextField.page || 1);
                  setSelectedSignatureField(convertedField);
                }

                onSignatureViewClose();
                setShowSignaturePlacement(true);
              }}
              onClose={onSignatureViewClose}
            />
          </div>
        ) : (
          <div 
            ref={documentRef}
            className="relative mx-auto my-8 flex justify-center"
            style={{ maxWidth: '100%' }}
          >
            {fileUrl ? (
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => {
                  console.error('Error loading PDF:', error);
                  setPdfError(true);
                }}
                loading={
                  <div className="flex justify-center items-center h-96">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                }
                error={
                  <div className="text-center py-10">
                    <p className="text-red-500 mb-2">Failed to load PDF</p>
                    <p className="text-gray-600 text-sm">
                      Please try downloading the document instead
                    </p>
                  </div>
                }
                className="flex justify-center"
              >
                {viewMode === 'continuous' ? (
                  // Continuous scroll mode - render multiple pages
                  <div 
                    ref={scrollContainerRef}
                    className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto px-4"
                    style={{
                      scrollBehavior: 'smooth'
                    }}
                  >
                    {Array.from({ length: numPages }, (_, index) => {
                      const currentPageNum = index + 1;
                      // Lazy loading: only render visible pages + buffer
                      const shouldRender = visiblePages.has(currentPageNum) || 
                                         visiblePages.has(currentPageNum - 1) || 
                                         visiblePages.has(currentPageNum + 1) ||
                                         currentPageNum === 1; // Always render first page
                      
                      return (
                        <div
                          key={`page-${currentPageNum}`}
                          ref={el => {
                            if (el) {
                              pageRefs.current[currentPageNum] = el;
                              // Start observing when element is added
                              if (observerRef.current && viewMode === 'continuous') {
                                observerRef.current.observe(el);
                              }
                            }
                          }}
                          data-page-number={currentPageNum}
                          className="relative mx-auto"
                          style={{
                            minHeight: pageHeights[currentPageNum] || '842px', // A4 default height
                            marginBottom: '24px',
                            borderRadius: '8px',
                            boxShadow: shouldRender ? '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.05)' : 'none',
                            background: shouldRender ? 'white' : '#f3f4f6',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          {shouldRender ? (
                            <>
                              <Page 
                                pageNumber={currentPageNum} 
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                                scale={scale}
                                className="shadow-lg"
                                width={window.innerWidth > 1024 ? 800 : undefined}
                                onLoadSuccess={(page) => {
                                  // Store page dimensions for coordinate calculations
                                  const { width, height } = page;
                                  setPageHeights(prev => ({
                                    ...prev,
                                    [currentPageNum]: height * scale
                                  }));
                                  setAllPageDimensions(prev => ({
                                    ...prev,
                                    [currentPageNum]: { width, height }
                                  }));
                                  if (currentPageNum === 1) {
                                    onPageLoadSuccess(page);
                                  }
                                }}
                              />
                              {/* Render signatures for this specific page */}
                              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 20 }}>
                                {(() => {
                                  const pageElement = pageRefs.current[currentPageNum]?.querySelector('.react-pdf__Page__canvas');
                                  if (!pageElement) return null;
                                  const elements = renderSignaturesForPage(currentPageNum, pageElement);
                                  return elements.map((element, idx) => (
                                    <div key={`sig-${currentPageNum}-${idx}`} className="pointer-events-auto">
                                      {element}
                                    </div>
                                  ));
                                })()}
                              </div>
                              {/* Render form fields for this specific page */}
                              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 25 }}>
                                {(() => {
                                  const pageElement = pageRefs.current[currentPageNum]?.querySelector('.react-pdf__Page__canvas');
                                  if (!pageElement) return null;
                                  const elements = renderFormFieldsForPage(currentPageNum, pageElement);
                                  return elements.map((element, idx) => (
                                    <div key={`field-${currentPageNum}-${idx}`} className="pointer-events-auto">
                                      {element}
                                    </div>
                                  ));
                                })()}
                              </div>
                              {/* Render annotations for this specific page */}
                              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 30 }}>
                                {(() => {
                                  const elements = renderAnnotationsForPage(currentPageNum);
                                  return elements.map((element, idx) => (
                                    <div key={`annotation-${currentPageNum}-${idx}`} className="pointer-events-auto">
                                      {element}
                                    </div>
                                  ));
                                })()}
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                              <div className="text-center">
                                <FileText size={48} className="mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Page {currentPageNum}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Page number indicator */}
                          <div 
                            className="absolute bottom-4 right-4 px-3 py-1 bg-gray-800 bg-opacity-60 text-white text-xs rounded-full"
                            style={{
                              zIndex: 10,
                              backdropFilter: 'blur(4px)'
                            }}
                          >
                            Page {currentPageNum}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Paged mode - single page view
                  <Page 
                    pageNumber={pageNumber} 
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    scale={scale}
                    className="shadow-lg"
                    width={window.innerWidth > 1024 ? 800 : undefined}
                    onLoadSuccess={onPageLoadSuccess}
                  />
                )}
              </Document>
            ) : (
              <div className="flex justify-center items-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            
            {/* In paged mode only - render signatures, form fields, annotations globally */}
            {viewMode === 'paged' && (
              <>
                {/* Render all signatures */}
                {renderAllSignatures()}
                
                {/* Render form fields */}
                {renderAllFormFields()}
                
                {/* Render annotations */}
                {renderAnnotations()}
              </>
            )}

            {/* Floating Pagination Mini-Bar - Bottom Center */}
            {numPages && numPages > 1 && (
              <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
                <div 
                  className="bg-white rounded-full shadow-lg border px-4 py-2 flex items-center space-x-3"
                  style={{
                    fontFamily: fontStyles.fontFamily,
                    borderColor: colors.gray200,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                    backdropFilter: 'blur(12px)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
                  }}
                >
                  <button
                    onClick={previousPage}
                    disabled={pageNumber <= 1}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
                    style={{
                      color: pageNumber <= 1 ? colors.gray300 : colors.gray700
                    }}
                    title="Previous page"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <div 
                    className="px-4 py-1 text-sm font-medium select-none"
                    style={{
                      color: colors.gray900,
                      ...fontStyles.medium,
                      letterSpacing: '-0.01em',
                      minWidth: '100px',
                      textAlign: 'center'
                    }}
                  >
                    Page {pageNumber} of {numPages}
                  </div>
                  
                  <button
                    onClick={nextPage}
                    disabled={pageNumber >= numPages}
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
                    style={{
                      color: pageNumber >= numPages ? colors.gray300 : colors.gray700
                    }}
                    title="Next page"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* Floating Next Field Button - appears when header is not visible, positioned next to page controls */}
            {!isHeaderVisible && calculateRemainingFields && navigateToNextField && (() => {
              const remainingFields = calculateRemainingFields();
              const hasFieldsToSign = remainingFields > 0;
              
              return hasFieldsToSign ? (
                <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 translate-x-40 z-50">
                  <button
                    onClick={navigateToNextField}
                    className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, #1e40af, #1e3a8a)',
                      color: '#ffffff',
                      borderColor: '#1e40af',
                      fontWeight: 500,
                      letterSpacing: '-0.01em',
                      boxShadow: '0 4px 16px rgba(30, 64, 175, 0.2)',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      fontFamily: fontStyles.fontFamily
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(30, 64, 175, 0.3)';
                      e.target.style.filter = 'brightness(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 16px rgba(30, 64, 175, 0.2)';
                      e.target.style.filter = 'brightness(1)';
                    }}
                    title={`Click to go to next field (${remainingFields} remaining)`}
                  >
                    <FileSignature size={16} className="mr-2 transition-transform duration-300 group-hover:scale-110" />
                    Next Field ({remainingFields} left)
                  </button>
                </div>
              ) : null;
            })()}

            {/* Floating Zoom Controls Mini-Toolbar - Bottom Right */}
            <div className="fixed bottom-6 right-6 z-40 flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div 
                className="bg-white rounded-full shadow-lg border px-3 py-2 flex items-center"
                style={{
                  fontFamily: fontStyles.fontFamily,
                  borderColor: colors.gray200,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                  backdropFilter: 'blur(12px)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
                }}
              >
                <button 
                  onClick={() => setViewMode(viewMode === 'paged' ? 'continuous' : 'paged')}
                  className="p-2 rounded-full hover:bg-blue-50 text-gray-600 transition-all duration-200 group"
                  style={{
                    color: colors.gray700
                  }}
                  title={viewMode === 'paged' ? 'Switch to continuous scroll' : 'Switch to paged view'}
                >
                  {viewMode === 'paged' ? (
                    <Layers size={18} className="group-hover:text-blue-600 transition-colors" />
                  ) : (
                    <FileText size={18} className="group-hover:text-blue-600 transition-colors" />
                  )}
                </button>
                
                <div 
                  className="px-2 py-1 text-xs font-medium select-none border-l ml-2 pl-3"
                  style={{
                    color: colors.gray500,
                    borderColor: colors.gray200,
                    ...fontStyles.medium,
                    letterSpacing: '-0.01em'
                  }}
                >
                  {viewMode === 'paged' ? 'Page' : 'Scroll'}
                </div>
              </div>
              
              {/* Zoom Controls */}
              <div 
                className="bg-white rounded-full shadow-lg border px-3 py-2 flex items-center space-x-2"
                style={{
                  fontFamily: fontStyles.fontFamily,
                  borderColor: colors.gray200,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                  backdropFilter: 'blur(12px)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
                }}
              >
                <button 
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
                  style={{
                    color: scale <= 0.5 ? colors.gray300 : colors.gray700
                  }}
                  title="Zoom out"
                >
                  <ZoomOut size={18} />
                </button>
                
                <div 
                  className="px-3 py-1 text-sm font-medium select-none"
                  style={{
                    color: colors.gray900,
                    ...fontStyles.medium,
                    letterSpacing: '-0.01em',
                    minWidth: '65px',
                    textAlign: 'center'
                  }}
                >
                  {Math.round(scale * 100)}%
                </div>
                
                <button 
                  onClick={zoomIn}
                  disabled={scale >= 3}
                  className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
                  style={{
                    color: scale >= 3 ? colors.gray300 : colors.gray700
                  }}
                  title="Zoom in"
                >
                  <ZoomIn size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with actions */}
      <div className="bg-white border-t border-gray-200 p-4 flex justify-between">
        {successMessage && appliedSignatures.length === 0 && (
          <div className="flex items-center text-green-600">
            <CheckCircle size={18} className="mr-2" />
            <span>{successMessage}</span>
          </div>
        )}
        <div className="ml-auto flex gap-4">
          {/* Show signature count when signatures are applied */}
          {appliedSignatures.length > 0 && (
            <div className="flex items-center text-blue-600 bg-blue-50 px-3 py-2 rounded-md">
              <FileSignature size={18} className="mr-2" />
              <span className="font-medium">{appliedSignatures.length} signature{appliedSignatures.length !== 1 ? 's' : ''} ready</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Signature placement overlay */}
      {showSignaturePlacement && (canUserSign() || canAddSignatureAnytime()) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="container mx-auto p-4 h-full">
            <SignaturePlacement
              documentData={{
                url: fileUrl,
                name: document?.name || 'Document',
                pageCount: numPages
              }}
              onSignatureAdded={handleSignatureAdded}
              onCancel={() => {
                setShowSignaturePlacement(false);
                setSelectedSignatureField(null);
              }}
              documentId={documentId}
              user={user}
              API_URL={API_URL}
              signatureFields={signatureFields}
              selectedField={selectedSignatureField}
              initialField={editingSignatureField}
              isOwner={isOwner}
            />
          </div>
        </div>
      )}
      


      {/* Post-Signature Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Success Header */}
            <div className="bg-green-50 px-6 py-4 border-b border-green-100">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-green-900">
                    Signature Added Successfully!
                  </h3>
                  <p className="text-sm text-green-700">
                    {allRequiredFieldsSigned 
                      ? "All required fields have been completed"
                      : "Your signature has been applied to the document"
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                {allRequiredFieldsSigned ? (
                  <div className="text-center">
                    <p className="text-gray-700 mb-4">
                      Ready to complete the signing process? This will finalize the document and notify all parties.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                      <strong>Next step:</strong> Complete the document to make it official
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-700 mb-4">
                      You can add more signatures or complete the document now.
                    </p>
                    <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
                      <strong>Note:</strong> You can always complete the document later from the floating button
                    </div>
                  </div>
                )}
                
                {/* Legal Agreement Checkbox */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasReadContract}
                      onChange={(e) => setHasReadContract(e.target.checked)}
                      className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">I acknowledge that:</span> 
                      <br />• I have read and understand the entire contract/document
                      <br />• This electronic signature is legally binding and has the same legal effect as a handwritten signature
                      <br />• I am authorized to sign this document on behalf of myself or my organization
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-3">
              {allRequiredFieldsSigned ? (
                <>
                  <button
                    onClick={() => {
                      setShowCompletionModal(false);
                      finalizeDocument();
                    }}
                    disabled={!hasReadContract}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Complete Document Now
                  </button>
                  <button
                    onClick={() => setShowCompletionModal(false)}
                    className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Complete Later
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowCompletionModal(false)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Continue Signing
                  </button>
                  <button
                    onClick={() => {
                      setShowCompletionModal(false);
                      finalizeDocument();
                    }}
                    disabled={!hasReadContract}
                    className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Complete Now
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Completion Button - Shows when signatures are applied */}
      {appliedSignatures.length > 0 && !showCompletionModal && !showSignaturePlacement && !loading && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-white rounded-full shadow-lg border border-gray-200 p-1 animate-pulse">
            <button
              onClick={() => setShowCompletionModal(true)}
              className="bg-green-600 text-white rounded-full px-6 py-3 hover:bg-green-700 transition-colors font-medium shadow-lg hover:shadow-xl transform hover:scale-105 animate-none"
            >
              <CheckCircle size={20} className="mr-2" />
              Complete Document ({appliedSignatures.length})
            </button>
          </div>
          
          {/* Floating help text */}
          <div className="absolute bottom-full right-0 mb-2 bg-gray-900 text-white text-xs px-3 py-1 rounded-lg opacity-90 max-w-xs">
            {allRequiredFieldsSigned 
              ? "All fields completed - Click to complete!" 
              : "Click to finalize your signatures"
            }
            <br />
            <span className="text-gray-300">Review & confirm</span>
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}

      {/* Annotation Details Modal */}
      {showAnnotationModal && selectedAnnotation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              selectedAnnotation.aiGenerated ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {selectedAnnotation.aiGenerated ? (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        selectedAnnotation.content?.riskLevel === 'HIGH' ? 'bg-red-500' :
                        selectedAnnotation.content?.riskLevel === 'MEDIUM' ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}>
                        {selectedAnnotation.content?.riskLevel === 'HIGH' ? '!' : 
                         selectedAnnotation.content?.riskLevel === 'MEDIUM' ? '?' : 'i'}
                      </div>
                    ) : (
                      <MessageSquare className="w-8 h-8 text-blue-500" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-lg font-semibold ${
                      selectedAnnotation.aiGenerated ? 'text-red-900' : 'text-blue-900'
                    }`}>
                      {selectedAnnotation.aiGenerated ? 'AI Analysis' : 'Manual Annotation'}
                    </h3>
                    <p className={`text-sm ${
                      selectedAnnotation.aiGenerated ? 'text-red-700' : 'text-blue-700'
                    }`}>
                      {selectedAnnotation.content?.text || 'No title available'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnnotationModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {/* Risk Level (for AI annotations) */}
                {selectedAnnotation.aiGenerated && selectedAnnotation.content?.riskLevel && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Risk Level:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedAnnotation.content.riskLevel === 'HIGH' ? 'bg-red-100 text-red-800' :
                      selectedAnnotation.content.riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {selectedAnnotation.content.riskLevel}
                    </span>
                  </div>
                )}

                {/* Highlighted Text */}
                {selectedAnnotation.content?.searchText && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Highlighted Text:</h4>
                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                      <p className="text-sm text-gray-800 italic">
                        "{selectedAnnotation.content.searchText}"
                      </p>
                    </div>
                  </div>
                )}

                {/* Explanation */}
                {selectedAnnotation.content?.explanation && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis:</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {selectedAnnotation.content.explanation}
                    </p>
                  </div>
                )}

                {/* Suggestion */}
                {selectedAnnotation.content?.suggestion && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendation:</h4>
                    <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
                      <p className="text-sm text-blue-800">
                        {selectedAnnotation.content.suggestion}
                      </p>
                    </div>
                  </div>
                )}

                {/* Page Info */}
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>Page {selectedAnnotation.page || 1}</span>
                  {selectedAnnotation.aiGenerated && (
                    <span>AI Generated</span>
                  )}
                  <span>
                    Created: {new Date(selectedAnnotation.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowAnnotationModal(false)}
                className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              {selectedAnnotation.aiGenerated && (
                <button
                  onClick={() => {
                    // Navigate to the highlighted text
                    if (selectedAnnotation.page && selectedAnnotation.page !== pageNumber) {
                      setPageNumber(selectedAnnotation.page);
                    }
                    setShowAnnotationModal(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View in Document
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;